import { useState, useEffect, useMemo } from "react";
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { LayoutDashboard, Shirt, CalendarDays, Loader2, LogOut, RefreshCw, Eye, DollarSign, MapPin, Truck, Settings, Instagram, Clock, AlertTriangle, Trash2, Image, Shield, FileText, ChevronDown, ChevronUp, ChevronRight, Camera, Upload, Banknote, Wrench, Mail, Send, Copy, CheckCircle2, Pencil, Search, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { AdminLoadingState, AdminApiError } from "../components/admin/AdminErrorBoundary";
import { EmailStatusBadge } from "../components/admin/EmailStatus";
import { BookingExchangeModal } from "../components/admin/BookingExchangeModal";
import { SmsReplyModal } from "../components/admin/SmsReplyModal";
import { BookingActivityTimeline } from "../components/admin/BookingActivityTimeline";
import { Timeline } from "../components/admin/Timeline";
import { BookingNotesPanel } from "../components/admin/BookingNotesPanel";
// Phase 5D — legacy AdminManualBookingModal removed (Sep 2026).
// AdminManualBookingGroupModal is now the single source of truth for all
// manual bookings (single- and multi-item).
import { AdminManualBookingGroupModal } from "../components/admin/AdminManualBookingGroupModal";
import { ChargeCollectionOptions } from "../components/admin/charges/ChargeCollectionOptions";
import { EditChargeDialog } from "../components/admin/charges/EditChargeDialog";
import { TrialLeadPicker } from "../components/admin/TrialLeadPicker";
import { ArrowRightLeft, History, StickyNote, UserPlus, Scale } from "lucide-react";
import { SettlementPanel, SettlementBadge } from "../components/admin/settlement/SettlementPanel";
import { SettledBondLine, ChargeSettlementBadges, adoptedFromHistory, settledViaWorkflow } from "../components/admin/settlement/SettledDetails";
import AdminSidebar from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = { pending_payment: "bg-amber-50 text-amber-700 border-amber-200", confirmed: "bg-blue-50 text-blue-700 border-blue-200", active: "bg-green-50 text-green-700 border-green-200", completed: "bg-gray-50 text-gray-700 border-gray-200", cancelled: "bg-red-50 text-red-700 border-red-200", expired: "bg-red-50 text-red-700 border-red-200" };
const statusLabels = { pending_payment: "Awaiting Payment", confirmed: "Confirmed", active: "Active", completed: "Completed", cancelled: "Cancelled", expired: "Expired" };
const paymentStatusColors = { pending: "bg-yellow-50 text-yellow-700 border-yellow-200", partial: "bg-amber-50 text-amber-700 border-amber-200", paid: "bg-green-50 text-green-700 border-green-200", deposit_refunded: "bg-purple-50 text-purple-700 border-purple-200", partial_refund: "bg-purple-50 text-purple-700 border-purple-200", refunded: "bg-gray-100 text-gray-600 border-gray-200" };

// Phase 1 — Payment Summary enums
const RENTAL_METHOD_OPTIONS = [
  { value: "stripe", label: "Stripe" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "eftpos", label: "EFTPOS" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];
const BOND_METHOD_OPTIONS = RENTAL_METHOD_OPTIONS;
const RENTAL_STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "unpaid", label: "Unpaid" },
];
const BOND_STATUS_V2_OPTIONS = [
  { value: "collected", label: "Bond Collected" },
  { value: "not_collected", label: "Bond Not Collected" },
  { value: "refunded", label: "Bond Refunded" },
  { value: "partially_deducted", label: "Bond Partially Deducted" },
  { value: "fully_deducted", label: "Bond Fully Deducted" },
];
const _labelOf = (opts, v) => (opts.find((o) => o.value === v) || {}).label || (v ? v.replace(/_/g, " ") : "—");
const rentalStatusBadgeColor = (v) => ({
  paid: "bg-green-50 text-green-700 border-green-200",
  partially_paid: "bg-amber-50 text-amber-700 border-amber-200",
  unpaid: "bg-red-50 text-red-700 border-red-200",
}[v] || "bg-gray-50 text-gray-600 border-gray-200");
const bondStatusBadgeColor = (v) => ({
  collected: "bg-green-50 text-green-700 border-green-200",
  refunded: "bg-purple-50 text-purple-700 border-purple-200",
  partially_deducted: "bg-amber-50 text-amber-700 border-amber-200",
  fully_deducted: "bg-red-50 text-red-700 border-red-200",
  not_collected: "bg-gray-50 text-gray-600 border-gray-200",
}[v] || "bg-gray-50 text-gray-600 border-gray-200");

// Phase 1 — unlock gate: Paid OR Partially Paid both unlock Return / Damage / Inspection.
const isPaymentUnlocked = (b) => {
  if (!b) return false;
  const v2 = b.rental_payment_status_v2;
  if (v2 === "paid" || v2 === "partially_paid") return true;
  return ["paid", "partial"].includes(b.payment_status);
};

// Human-friendly label for the payment-status badge.
// "partial_refund" in our flow always means "rental kept, bond refunded" — surface that plainly.
const paymentStatusLabel = (s) => ({
  pending: "Pending",
  paid: "Paid",
  partial_refund: "Bond Refunded",
  deposit_refunded: "Bond Refunded",
  refunded: "Refunded",
}[s] || (s || "").replace(/_/g, " "));

export default function AdminBookings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [smsModal, setSmsModal] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [returnDate, setReturnDate] = useState(format(new Date(), "yyyy-MM-dd"));
  // Phase 5D — single Manual Booking modal (legacy AdminManualBookingModal
  // removed; AdminManualBookingGroupModal now handles single- and multi-item).
  const [showManualBooking, setShowManualBooking] = useState(false);
  // Phase 5D — edit an existing manual booking group
  const [editGroupId, setEditGroupId] = useState(null);
  // Legacy pseudo-group edit — accepts { anchorId, peerIds[] } and passes both
  // to the modal so the backend can promote the whole set on save.
  const [editLegacyGroup, setEditLegacyGroup] = useState(null);

  // Phase 1 — booking search (matches full + shortened IDs, plus customer/outfit text)
  const [searchQuery, setSearchQuery] = useState("");

  // Phase 4 — Outstanding Balances strip
  const [outstanding, setOutstanding] = useState(null);
  const fetchOutstanding = async () => {
    try {
      const r = await axios.get(`${API}/admin/outstanding-balances`, getAuthHeaders());
      setOutstanding(r.data);
    } catch { /* ignore — strip just won't render */ }
  };
  const outstandingByBooking = useMemo(() => {
    const m = {};
    (outstanding?.items || []).forEach((it) => { m[it.booking_id] = +((m[it.booking_id] || 0) + Number(it.amount || 0)).toFixed(2); });
    return m;
  }, [outstanding]);
  const OutstandingBadge = ({ bookingId }) => (outstandingByBooking[bookingId] ? (
    <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200" data-testid={`outstanding-badge-${bookingId}`}>
      Owes ${outstandingByBooking[bookingId].toFixed(2)}
    </span>
  ) : null);
  useEffect(() => {
    // Deep-link from Dashboard ?q=<bookingId>
    const u = new URL(window.location.href);
    const q = u.searchParams.get("q");
    if (q) setSearchQuery(q);
    fetchOutstanding();
  }, []);

  // Phase 1 — Payment Summary edit modal state
  const [isPaymentSummaryOpen, setIsPaymentSummaryOpen] = useState(false);
  const [paymentSummaryDraft, setPaymentSummaryDraft] = useState(null);
  const [paymentSummarySaving, setPaymentSummarySaving] = useState(false);
  const openPaymentSummary = (b) => {
    setPaymentSummaryDraft({
      rental_payment_method: b.rental_payment_method || "other",
      rental_payment_status: b.rental_payment_status_v2 || (b.payment_status === "paid" ? "paid" : b.payment_status === "partial" ? "partially_paid" : "unpaid"),
      rental_amount_paid: b.rental_amount_paid != null ? String(b.rental_amount_paid) : "",
      rental_paid_at: (b.rental_paid_at || "").slice(0, 10),
      bond_collection_method: b.bond_collection_method || "other",
      bond_status_v2: b.bond_status_v2 || (b.bond_status === "refunded" ? "refunded" : b.payment_status === "paid" ? "collected" : "not_collected"),
      bond_collected_at: (b.bond_collected_at || "").slice(0, 10),
      internal_payment_notes: b.internal_payment_notes || "",
    });
    setIsPaymentSummaryOpen(true);
  };
  const savePaymentSummary = async () => {
    if (!selectedBooking || !paymentSummaryDraft) return;
    setPaymentSummarySaving(true);
    try {
      const body = { ...paymentSummaryDraft };
      // numeric coercion
      if (body.rental_amount_paid === "") delete body.rental_amount_paid;
      else body.rental_amount_paid = Number(body.rental_amount_paid);
      const res = await axios.patch(
        `${API}/admin/bookings/${selectedBooking.id}/payment-summary`,
        body,
        getAuthHeaders()
      );
      toast.success(res.data?.no_op ? "No changes" : "Payment summary updated");
      if (res.data?.booking) setSelectedBooking(res.data.booking);
      setIsPaymentSummaryOpen(false);
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setPaymentSummarySaving(false);
    }
  };
  const copyBookingId = (id) => {
    try {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(id);
        toast.success(`Copied ${id}`);
      } else {
        const ta = document.createElement("textarea");
        ta.value = id; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(`Copied ${id}`);
      }
    } catch { toast.error("Copy failed"); }
  };

  useEffect(() => { checkAuth(); fetchBookings(); }, []);

  const checkAuth = async () => { const token = localStorage.getItem("adminToken"); if (!token) navigate("/admin/login"); };
  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } });

  const fetchBookings = async () => {
    try { 
      setFetchError(null);
      const response = await axios.get(`${API}/bookings`, getAuthHeaders()); 
      setBookings(response.data || []); 
      return response.data || [];
    } 
    catch (err) { 
      console.error("[AdminBookings] Failed to load:", err);
      setFetchError("Failed to load bookings. Please refresh or contact support.");
    } 
    finally { setLoading(false); }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try { setProcessing(true); await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`, {}, getAuthHeaders()); toast.success(`Status updated to ${newStatus}`); fetchBookings(); } 
    catch { toast.error("Error updating status"); } 
    finally { setProcessing(false); }
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking) return;
    try {
      setProcessing(true);
      await axios.delete(`${API}/bookings/${selectedBooking.id}`, getAuthHeaders());
      toast.success("Booking deleted successfully");
      fetchBookings();
      setIsDeleteDialogOpen(false);
      setIsDialogOpen(false);
      setSelectedBooking(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting booking");
    } finally {
      setProcessing(false);
    }
  };

  const handleFullRefund = async () => {
    if (!selectedBooking) return;
    const amount = refundAmount ? parseFloat(refundAmount) : null;
    try {
      setProcessing(true);
      const res = await axios.post(
        `${API}/admin/bookings/${selectedBooking.id}/full-refund`,
        { amount, reason: refundReason || "Admin refund" },
        getAuthHeaders()
      );
      toast.success(`Refund of $${res.data.amount.toFixed(2)} processed successfully`);
      fetchBookings();
      setIsRefundDialogOpen(false);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Refund failed");
    } finally {
      setProcessing(false);
    }
  };

  const [isBondDialogOpen, setIsBondDialogOpen] = useState(false);
  const [bondAction, setBondAction] = useState("refund_full");
  const [bondRefundAmount, setBondRefundAmount] = useState("");
  const [bondReason, setBondReason] = useState("");

  // Phase 2 — Bond Refund modal (unified: External + Stripe path)
  const [bondRefundOpen, setBondRefundOpen] = useState(false);
  const [bondRefundSaving, setBondRefundSaving] = useState(false);
  const [bondRefundDraft, setBondRefundDraft] = useState(null);
  // Remaining refundable bond = deposit − charge deductions − prior refunds − prior retained.
  const refundableBond = (b) => {
    if (chargesMeta?.booking_id === b?.id && chargesMeta?.bond_summary?.remaining_refundable !== undefined) {
      return Number(chargesMeta.bond_summary.remaining_refundable) || 0;
    }
    if (b?.bond_closed) return 0;
    const deposit = Number(b?.deposit_amount) || 0;
    const prior = (Number(b?.bond_charges_deducted_total) || 0) + (b && "bond_closed" in b ? (Number(b.bond_refund_amount) || 0) + (Number(b.bond_deduction_amount) || 0) : 0);
    return Math.max(+(deposit - prior).toFixed(2), 0);
  };
  const bondRefundNotice = (b) => {
    if (!b || chargesMeta?.booking_id !== b.id) return null;
    const deducted = Number(chargesMeta?.bond_summary?.deducted_by_charges) || 0;
    const owed = Number(chargesMeta?.outstanding_total) || 0;
    const priorRefunded = Number(chargesMeta?.bond_summary?.refunded_total) || 0;
    const priorRetained = Number(chargesMeta?.bond_summary?.retained_total) || 0;
    if (!deducted && !owed && !chargesMeta?.needs_review_count && !priorRefunded && !priorRetained) return null;
    return (
      <div className="space-y-2" data-testid="bond-refund-notice">
        {(priorRefunded > 0 || priorRetained > 0) && (
          <div className="text-[11px] px-3 py-2 bg-[#FAF8F0] border border-[#9A7B4F]/25 text-[#676664]" data-testid="bond-refund-prior-info">
            Already refunded ${priorRefunded.toFixed(2)}{priorRetained > 0 ? ` · retained $${priorRetained.toFixed(2)}` : ""}. Still refundable: <strong>${refundableBond(b).toFixed(2)}</strong>.
          </div>
        )}
        {deducted > 0 && (
          <div className="text-[11px] px-3 py-2 bg-[#FAF8F0] border border-[#9A7B4F]/25 text-[#676664]" data-testid="bond-refund-deducted-info">
            ${deducted.toFixed(2)} of this bond was already deducted for charges. Refundable bond: <strong>${refundableBond(b).toFixed(2)}</strong>.
          </div>
        )}
        {owed > 0 && (
          <div className="text-[11px] px-3 py-2 bg-amber-50 border border-amber-300 text-amber-800 flex gap-2" data-testid="bond-refund-outstanding-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>This customer still owes <strong>${owed.toFixed(2)}</strong> across {chargesMeta.outstanding_count} unpaid charge{chargesMeta.outstanding_count === 1 ? "" : "s"}. Consider deducting it from the bond before refunding.</span>
          </div>
        )}
        {chargesMeta?.needs_review_count > 0 && (
          <div className="text-[11px] px-3 py-2 bg-orange-50 border border-orange-200 text-orange-800" data-testid="bond-refund-review-warning">
            {chargesMeta.needs_review_count} historical charge{chargesMeta.needs_review_count === 1 ? "" : "s"} still need review on this booking.
          </div>
        )}
      </div>
    );
  };
  const openBondRefund = (b) => {
    if (!b) return;
    const deposit = refundableBond(b);
    const isStripe = (b.bond_collection_method || "").toLowerCase() === "stripe";
    setBondRefundDraft({
      refund_path: isStripe ? "stripe" : "external",
      refund_type: "full_refund",
      refund_amount: String(deposit.toFixed(2)),
      deduction_amount: "0.00",
      refund_method: isStripe ? "stripe" : (b.bond_collection_method && b.bond_collection_method !== "other" ? b.bond_collection_method : "bank_transfer"),
      refund_date: format(new Date(), "yyyy-MM-dd"),
      deduction_reason: "",
      internal_notes: "",
      customer_notes: "",
      notify_customer: true,
    });
    setBondRefundOpen(true);
  };
  const applyBondRefundType = (rtype) => {
    if (!bondRefundDraft || !selectedBooking) return;
    const deposit = refundableBond(selectedBooking);
    let refund = Number(bondRefundDraft.refund_amount) || 0;
    let ded = Number(bondRefundDraft.deduction_amount) || 0;
    if (rtype === "full_refund") { refund = deposit; ded = 0; }
    else if (rtype === "full_deduction") { refund = 0; ded = deposit; }
    else if (rtype === "partial_refund") {
      if (refund <= 0 || refund >= deposit) refund = Math.max(0.01, Math.min(deposit - 0.01, deposit / 2));
      ded = 0;
    }
    else if (rtype === "partial_deduction") {
      if (refund + ded > deposit) { refund = deposit / 2; ded = deposit / 2; }
      if (ded <= 0) ded = deposit / 2;
    }
    setBondRefundDraft({ ...bondRefundDraft, refund_type: rtype, refund_amount: refund.toFixed(2), deduction_amount: ded.toFixed(2) });
  };
  const submitBondRefund = async () => {
    if (!selectedBooking || !bondRefundDraft) return;
    setBondRefundSaving(true);
    try {
      if (bondRefundDraft.refund_path === "stripe") {
        // Use the existing Stripe-backed endpoint (no behaviour change for website bookings)
        const action = bondRefundDraft.refund_type === "full_refund" ? "refund_full"
                     : bondRefundDraft.refund_type === "partial_refund" ? "refund_partial"
                     : "retain";
        const body = {
          action,
          refund_amount: action === "refund_partial" ? Number(bondRefundDraft.refund_amount) || 0 : null,
          reason: bondRefundDraft.deduction_reason || bondRefundDraft.customer_notes || "",
        };
        const res = await axios.patch(`${API}/bookings/${selectedBooking.id}/refund-deposit`, body, getAuthHeaders());
        const left = res.data?.bond_closed === false ? ` · $${Number(res.data.remaining_refundable).toFixed(2)} still held` : "";
        toast.success(`${res.data?.message || "Stripe refund processed"}${left}`);
      } else {
        const body = {
          refund_type: bondRefundDraft.refund_type,
          refund_amount: Number(bondRefundDraft.refund_amount) || 0,
          deduction_amount: Number(bondRefundDraft.deduction_amount) || 0,
          refund_method: bondRefundDraft.refund_method,
          refund_date: bondRefundDraft.refund_date,
          deduction_reason: bondRefundDraft.deduction_reason,
          internal_notes: bondRefundDraft.internal_notes,
          customer_notes: bondRefundDraft.customer_notes,
          notify_customer: !!bondRefundDraft.notify_customer,
        };
        const res = await axios.post(`${API}/admin/bookings/${selectedBooking.id}/refund-bond-external`, body, getAuthHeaders());
        const r = res.data || {};
        const parts = [
          `Bond ${r.bond_status_v2 === "refunded" ? "refunded" : r.bond_status_v2 === "fully_deducted" ? "fully deducted" : "updated"}`,
          r.refund_amount > 0 ? `· $${Number(r.refund_amount).toFixed(2)} refunded` : null,
          r.deduction_amount > 0 ? `· $${Number(r.deduction_amount).toFixed(2)} retained` : null,
          r.email_sent ? "· customer notified" : (bondRefundDraft.notify_customer && r.refund_amount > 0 ? "· email pending" : null),
          r.bond_closed === false ? `· $${Number(r.remaining_refundable).toFixed(2)} still held` : null,
        ].filter(Boolean);
        toast.success(parts.join(" "));
        if (r.booking) setSelectedBooking(r.booking);
      }
      setBondRefundOpen(false);
      fetchBookings();
      fetchCharges(selectedBooking.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bond refund failed");
    } finally {
      setBondRefundSaving(false);
    }
  };

  // Phase 3 — Charges (Damage / Late / Cleaning / Missing / Other)
  const CHARGE_TYPE_OPTIONS = [
    { value: "damage", label: "Damage / Repair" },
    { value: "cleaning", label: "Cleaning Fee" },
    { value: "late_fee", label: "Late Fee" },
    { value: "missing", label: "Missing Item Fee" },
    { value: "other", label: "Other Charge" },
  ];
  const COLLECTION_MODE_LABELS = {
    stripe_saved_card: "Saved card charged",
    stripe: "Stripe payment link",
    external_paid: "Paid externally",
    external_pending: "Collect externally later",
    bond_deduction: "Deducted from bond",
  };
  const chargeStatusBadge = (s) => ({
    paid: "bg-green-50 text-green-700 border-green-200",
    awaiting_external_payment: "bg-amber-50 text-amber-700 border-amber-200",
    stripe_invoice_sent: "bg-blue-50 text-blue-700 border-blue-200",
    needs_review: "bg-orange-50 text-orange-700 border-orange-200",
    cancelled: "bg-gray-100 text-gray-500 border-gray-200",
  }[s] || "bg-gray-50 text-gray-600 border-gray-200");
  const chargeStatusLabel = (s) => ({
    paid: "Paid",
    awaiting_external_payment: "Outstanding",
    stripe_invoice_sent: "Stripe Link Sent",
    needs_review: "Needs Review",
    cancelled: "Cancelled",
  }[s] || s || "");

  const [charges, setCharges] = useState([]);
  const [chargesMeta, setChargesMeta] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const fetchCharges = async (bookingId) => {
    if (!bookingId) return;
    setChargesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings/${bookingId}/charges`, getAuthHeaders());
      setCharges(res.data?.charges || []);
      setChargesMeta(res.data || null);
    } catch {
      setCharges([]);
      setChargesMeta(null);
    } finally {
      setChargesLoading(false);
    }
  };

  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeSaving, setChargeSaving] = useState(false);
  const [chargeDraft, setChargeDraft] = useState(null);
  const openCreateCharge = (preselectType = "damage", preset = {}) => {
    const today = format(new Date(), "yyyy-MM-dd");
    setChargeDraft({
      charge_type: preselectType,
      amount: "",
      description: "",
      internal_notes: "",
      customer_notes: "",
      photos: [],
      collection_mode: "external_paid",
      payment_method: "bank_transfer",
      payment_date: today,
      bond_amount: "",
      remainder_mode: "stripe",
      remainder_payment_method: "bank_transfer",
      remainder_payment_date: today,
      notify_customer: true,
      idempotency_key: (window.crypto?.randomUUID?.() || `req-${Date.now()}-${Math.random().toString(36).slice(2)}`),
      ...preset,
    });
    setChargeOpen(true);
    if (selectedBooking?.id) fetchCharges(selectedBooking.id);
  };
  const submitCharge = async () => {
    if (!selectedBooking || !chargeDraft) return;
    if (!chargeDraft.amount || Number(chargeDraft.amount) <= 0) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    const isBond = chargeDraft.collection_mode === "bond_deduction";
    if (isBond && (!chargeDraft.bond_amount || Number(chargeDraft.bond_amount) <= 0)) {
      toast.error("Enter how much to take from the bond");
      return;
    }
    if (isBond && Number(chargeDraft.bond_amount) > Number(chargeDraft.amount)) {
      toast.error("Bond amount can't be more than the charge");
      return;
    }
    setChargeSaving(true);
    try {
      const body = {
        charge_type: chargeDraft.charge_type,
        amount: Number(chargeDraft.amount),
        description: chargeDraft.description,
        internal_notes: chargeDraft.internal_notes,
        customer_notes: chargeDraft.customer_notes,
        photos: chargeDraft.photos,
        collection_mode: chargeDraft.collection_mode,
        notify_customer: !!chargeDraft.notify_customer,
        origin_url: window.location.origin,
        idempotency_key: chargeDraft.idempotency_key,
      };
      if (chargeDraft.collection_mode === "external_paid") {
        body.payment_method = chargeDraft.payment_method;
        body.payment_date = chargeDraft.payment_date;
      }
      if (isBond) {
        body.bond_amount = Number(chargeDraft.bond_amount);
        if (Number(chargeDraft.amount) - body.bond_amount > 0.001) {
          body.remainder_mode = chargeDraft.remainder_mode;
          body.remainder_payment_method = chargeDraft.remainder_payment_method;
          body.remainder_payment_date = chargeDraft.remainder_payment_date;
        }
      }
      const res = await axios.post(`${API}/admin/bookings/${selectedBooking.id}/charges`, body, getAuthHeaders());
      const rows = res.data?.charges || [res.data?.charge].filter(Boolean);
      const parts = rows.map((c) => `$${Number(c.amount).toFixed(2)} ${COLLECTION_MODE_LABELS[c.collection_mode]?.toLowerCase() || ""}`.trim());
      const tail = res.data?.email_sent ? " · customer notified" : "";
      toast.success(`Charge recorded · ${parts.join(" + ")}${tail}`);
      setChargeOpen(false);
      fetchCharges(selectedBooking.id);
      fetchOutstanding();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to create charge"));
    } finally {
      setChargeSaving(false);
    }
  };

  const markChargePaidInline = async (chargeId) => {
    const method = window.prompt("Payment method (bank_transfer / eftpos / cash / other)?", "bank_transfer");
    if (!method) return;
    try {
      await axios.post(`${API}/admin/bookings/charges/${chargeId}/mark-paid`, {
        payment_method: method,
        payment_date: format(new Date(), "yyyy-MM-dd"),
        notify_customer: true,
      }, getAuthHeaders());
      toast.success("Charge marked as paid");
      fetchCharges(selectedBooking?.id);
      fetchOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark paid");
    }
  };

  const editChargeInline = (charge) => setEditingCharge(charge);
  const deleteChargeInline = async (charge) => {
    const label = `${charge.charge_type_label || charge.charge_type} $${(charge.amount || 0).toFixed(2)}`;
    const bondNote = charge.payment_method === "bond_deduction" ? " The amount will go back into the refundable bond." : "";
    if (!window.confirm(`Delete charge "${label}"? This cannot be undone.${bondNote}`)) return;
    try {
      await axios.delete(`${API}/admin/bookings/charges/${charge.id}`, getAuthHeaders());
      toast.success("Charge deleted");
      fetchCharges(selectedBooking?.id);
      fetchOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete charge");
    }
  };

  // Damage Assessment State
  const [isDamageDialogOpen, setIsDamageDialogOpen] = useState(false);
  const [damageStatus, setDamageStatus] = useState("normal");
  const [repairCost, setRepairCost] = useState("");
  const [rrpOverride, setRrpOverride] = useState("");
  const [damageNotes, setDamageNotes] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [waiveCharges, setWaiveCharges] = useState(false);
  const [damageProcessing, setDamageProcessing] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Inspection Photo State
  const [isInspectionDialogOpen, setIsInspectionDialogOpen] = useState(false);
  const [activeBookingTab, setActiveBookingTab] = useState("details");
  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [inspectionCondition, setInspectionCondition] = useState("good");
  const [inspectionNotes, setInspectionNotes] = useState("");
  const [inspectionPhotos, setInspectionPhotos] = useState([]);
  const [inspectionProcessing, setInspectionProcessing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [existingInspection, setExistingInspection] = useState(null);
  const [emailResending, setEmailResending] = useState(null);

  const damageStatusLabels = {
    normal: "Normal",
    slightly_damaged: "Slightly Damaged",
    irreparable: "Irreparable",
    lost: "Lost",
    not_returned: "Not Returned",
  };
  const damageStatusColors = {
    normal: "bg-green-50 text-green-700 border-green-200",
    slightly_damaged: "bg-yellow-50 text-yellow-700 border-yellow-200",
    irreparable: "bg-red-50 text-red-700 border-red-200",
    lost: "bg-red-50 text-red-700 border-red-200",
    not_returned: "bg-orange-50 text-orange-700 border-orange-200",
  };

  const openDamageDialog = (booking) => {
    setDamageStatus(booking.damage_status || "normal");
    setRepairCost("");
    setRrpOverride(booking.rrp_at_booking ? String(booking.rrp_at_booking) : "");
    setDamageNotes(booking.damage_notes || "");
    setOverrideReason("");
    setWaiveCharges(false);
    setIsDamageDialogOpen(true);
    fetchAuditLog(booking.id);
  };

  const fetchAuditLog = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/audit-log`, getAuthHeaders());
      setAuditLogs(res.data);
    } catch { setAuditLogs([]); }
  };

  const handleDamageAssessment = async () => {
    if (!selectedBooking) return;
    if (damageStatus === "slightly_damaged" && !repairCost && !waiveCharges) {
      toast.error("Please enter repair cost or waive charges");
      return;
    }
    if (["irreparable", "lost", "not_returned"].includes(damageStatus) && !rrpOverride && !waiveCharges) {
      toast.error("RRP is required. Please set it on the product or enter an override.");
      return;
    }
    try {
      setDamageProcessing(true);
      const body = {
        damage_status: damageStatus,
        damage_notes: damageNotes,
        waive_charges: waiveCharges,
        override_reason: overrideReason,
      };
      if (damageStatus === "slightly_damaged") body.repair_cost = parseFloat(repairCost) || 0;
      if (["irreparable", "lost", "not_returned"].includes(damageStatus) && rrpOverride) {
        body.rrp_override = parseFloat(rrpOverride);
      }
      const res = await axios.patch(`${API}/bookings/${selectedBooking.id}/damage-assessment`, body, getAuthHeaders());
      toast.success(res.data.action || "Assessment saved");
      fetchBookings();
      setIsDamageDialogOpen(false);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving assessment");
    } finally {
      setDamageProcessing(false);
    }
  };

  const handleRefundDeposit = async (bookingId) => {
    // Open the bond dialog instead of simple confirm
    setIsBondDialogOpen(true);
    setBondAction("refund_full");
    setBondRefundAmount(refundableBond(selectedBooking).toString());
    setBondReason("");
  };

  const handleBondAction = async () => {
    if (!selectedBooking) return;
    try {
      setProcessing(true);
      const body = {
        action: bondAction,
        refund_amount: bondAction === "refund_partial" ? parseFloat(bondRefundAmount) || 0 : null,
        reason: bondReason
      };
      const response = await axios.patch(
        `${API}/bookings/${selectedBooking.id}/refund-deposit`,
        body,
        getAuthHeaders()
      );
      toast.success(response.data.message);
      fetchBookings();
      setIsBondDialogOpen(false);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error processing bond action");
    } finally {
      setProcessing(false);
    }
  };

  const handleRecordReturn = async () => {
    if (!selectedBooking) return;
    try {
      setProcessing(true);
      const response = await axios.patch(
        `${API}/bookings/${selectedBooking.id}/record-return?actual_return_date=${returnDate}`,
        {},
        getAuthHeaders()
      );
      
      if (response.data.late_fee > 0) {
        toast.warning(
          `Return recorded. ${response.data.days_late} day(s) late. Late fee assessed: $${response.data.late_fee.toFixed(2)} — choose how to collect it.`
        );
        fetchBookings();
        setIsReturnDialogOpen(false);
        openCreateCharge("late_fee", {
          amount: response.data.late_fee.toFixed(2),
          description: `Late return fee (${response.data.days_late} day${response.data.days_late === 1 ? "" : "s"})`,
          collection_mode: "external_pending",
        });
        return;
      } else {
        toast.success("Return recorded on time! Full security bond* refundable.");
      }
      
      fetchBookings();
      setIsReturnDialogOpen(false);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error recording return");
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  // ──── Inspection Photos ────
  const openInspectionDialog = async (booking) => {
    setInspectionCondition("good");
    setInspectionNotes("");
    setInspectionPhotos([]);
    setExistingInspection(null);
    setIsInspectionDialogOpen(true);
    try {
      const res = await axios.get(`${API}/admin/bookings/${booking.id}/inspection`, getAuthHeaders());
      if (res.data && res.data.return_condition) {
        setExistingInspection(res.data);
        setInspectionCondition(res.data.return_condition);
        setInspectionNotes(res.data.notes || "");
        setInspectionPhotos(res.data.photo_urls || []);
      }
    } catch { /* no existing inspection */ }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedBooking) return;
    try {
      setUploadingPhoto(true);
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/bookings/${selectedBooking.id}/upload-inspection-photo`, fd, {
        ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      setInspectionPhotos(prev => [...prev, res.data.image_url]);
      toast.success("Photo uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingPhoto(false); e.target.value = ""; }
  };

  const handleSaveInspection = async () => {
    if (!selectedBooking) return;
    try {
      setInspectionProcessing(true);
      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/inspection`, {
        return_condition: inspectionCondition,
        photo_urls: inspectionPhotos,
        notes: inspectionNotes,
        auto_refund_deposit: inspectionCondition === "good",
      }, getAuthHeaders());
      toast.success(`Inspection saved: ${inspectionCondition}`);
      fetchBookings(); setIsInspectionDialogOpen(false); setIsDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error saving inspection"); }
    finally { setInspectionProcessing(false); }
  };
  const handleResendEmail = async (type) => {
    if (!selectedBooking) return;
    setEmailResending(type);
    try {
      await axios.post(`${API}/admin/emails/resend`, {
        booking_id: selectedBooking.id,
        email_type: type,
        force: true,
      }, getAuthHeaders());
      toast.success(type === "admin_notification" ? "Admin notification resent" : "Customer confirmation resent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend email");
    } finally {
      setEmailResending(null);
    }
  };

  // Phase 1 — filter by status + free-text search (matches full + shortened booking ID, customer, outfit, email)
  // Phase 5F — also match by admin_booking_group_id (MBG-*) so admins can
  // search for a group directly. Because parents are collapsed downstream,
  // matching ANY child on the MBG id keeps that group's row visible.
  const filteredBookings = (bookings || []).filter((b) => {
    if (statusFilter !== "all" && b.booking_status !== statusFilter) return false;
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return true;
    const hay = [
      b.id || "",
      b.customer_name || "",
      b.customer_email || "",
      b.outfit_name || "",
      b.admin_booking_group_id || "",
    ].join(" \u0001 ").toLowerCase();
    // Plain substring — naturally matches both `BK-34C31` and `BK-34C3192307`,
    // and now `MBG-92891712EC` / `mbg-92891712ec` / `92891712ec` for groups.
    return hay.includes(q);
  });

  // Phase 5D-post — collapse multi-item groups into ONE parent row and split
  // amounts into rent-vs-bond-vs-delivery so revenue is never conflated with
  // bond. Uses the persisted `group_pricing_snapshot` when present (safe,
  // read-only) and falls back to the child booking fields otherwise.
  //
  // Legacy grouping fix — bookings without `admin_booking_group_id` are also
  // grouped into a "legacy pseudo-group" when ALL of these match:
  //   * exact normalized email (required, non-empty)
  //   * exact normalized phone (required IF both sides have one)
  //   * same event_date (required)
  //   * no conflicting customer_id
  // Grouping is NEVER inferred from customer name alone. Cancelled/expired
  // children are shown in the expanded view but EXCLUDED from active revenue.
  const [expandedGroups, setExpandedGroups] = useState({});
  const displayRows = useMemo(() => {
    const norm = (s) => (s || "").toString().trim().toLowerCase();
    const normPhone = (s) => {
      // Digits-only, then strip a leading "61" country code, then a leading
      // "0" trunk digit. This makes "+61 405110652" == "+61 0405110652" ==
      // "0405110652" == "405110652" — same person, common AU input variants.
      let d = (s || "").toString().replace(/[^\d]/g, "");
      if (d.startsWith("61")) d = d.slice(2);
      if (d.startsWith("0")) d = d.slice(1);
      return d;
    };
    const isCancelled = (b) => (b.booking_status || "").toLowerCase() === "cancelled";
    const isExpired   = (b) => (b.booking_status || "").toLowerCase() === "expired";
    const isCompleted = (b) => (b.booking_status || "").toLowerCase() === "completed";
    // Only genuinely cancelled items are excluded from active revenue.
    // Completed and expired items are historical but still valid rental revenue.
    const isExcludedFromRevenue = (b) => isCancelled(b);

    const bondAggregate = (children) => {
      const eligible = children.filter(c => !isCancelled(c));
      if (!eligible.length) return "—";
      const refunded = eligible.filter(c => (c.bond_status || "") === "refunded").length;
      const held = eligible.filter(c => (c.bond_status || "") === "held").length;
      if (refunded === eligible.length) return "refunded";
      if (refunded > 0 && held > 0) return "partially refunded";
      if (held === eligible.length) return "held";
      return eligible[0].bond_status || "—";
    };

    const byGroup = new Map();
    const legacyPool = [];
    for (const b of (filteredBookings || [])) {
      const gid = b.admin_booking_group_id;
      if (gid) {
        if (!byGroup.has(gid)) byGroup.set(gid, []);
        byGroup.get(gid).push(b);
      } else {
        legacyPool.push(b);
      }
    }

    // Legacy pseudo-grouping — bucket by (email, event_date), then split
    // buckets further by phone (when present) and customer_id conflicts.
    const legacyGroups = new Map(); // key -> [bookings]
    const legacyOrphans = [];
    for (const b of legacyPool) {
      const email = norm(b.customer_email);
      const eventDate = b.event_date;
      if (!email || !eventDate) { legacyOrphans.push(b); continue; }
      const bucketKey = `${email}::${eventDate}`;
      if (!legacyGroups.has(bucketKey)) legacyGroups.set(bucketKey, []);
      legacyGroups.get(bucketKey).push(b);
    }

    const legacyFinalGroups = []; // [{key, bookings}]
    for (const [bucketKey, arr] of legacyGroups.entries()) {
      if (arr.length < 2) { legacyOrphans.push(arr[0]); continue; }
      // Split by phone-conflict and customer_id-conflict.
      const remaining = [...arr];
      while (remaining.length) {
        const seed = remaining.shift();
        const seedPhone = normPhone(seed.customer_phone);
        const seedCid = seed.customer_id || null;
        const cluster = [seed];
        for (let i = remaining.length - 1; i >= 0; i--) {
          const cand = remaining[i];
          const candPhone = normPhone(cand.customer_phone);
          const candCid = cand.customer_id || null;
          // customer_id conflict → separate
          if (seedCid && candCid && seedCid !== candCid) continue;
          // phone conflict (both present + different) → separate; both empty is ok
          if (seedPhone && candPhone && seedPhone !== candPhone) continue;
          cluster.push(cand);
          remaining.splice(i, 1);
        }
        if (cluster.length >= 2) legacyFinalGroups.push({ key: `LEG-${bucketKey}`, bookings: cluster });
        else legacyOrphans.push(cluster[0]);
      }
    }

    const rows = [];
    // Real MBG-* groups first.
    for (const [gid, children] of byGroup.entries()) {
      const anchor = children[0];
      // Fallback: pull customer from first child with valid identity when
      // the anchor was blanked historically.
      const custName  = anchor.customer_name || children.find(c => c.customer_name)?.customer_name || "";
      const custEmail = anchor.customer_email || children.find(c => c.customer_email)?.customer_email || "";
      const snap = anchor.group_pricing_snapshot || {};
      const activeChildren = children.filter(c => !isCancelled(c));
      // If any child is currently cancelled we cannot trust the persisted
      // snapshot (it may have been captured while all children were active).
      // Recompute rent + bond from ACTIVE children instead.
      const hasCancelledChild = activeChildren.length !== children.length;
      const useSnap = !hasCancelledChild;
      const originalRent = Number((useSnap && snap.subtotal_original != null) ? snap.subtotal_original : activeChildren.reduce((s, c) => s + Number(c.rental_cost_base || c.rental_cost || 0), 0));
      const bundleDiscount = Number(useSnap ? (snap.bundle_discount_amount || 0) : 0);
      const adminDiscount = Number(useSnap ? (snap.admin_discount_amount_applied || 0) : 0);
      const activeRentSum = activeChildren.reduce((s, c) => s + Number(c.rental_cost || 0), 0);
      const actualRent = Number((useSnap && snap.post_admin_subtotal != null) ? snap.post_admin_subtotal : Math.max(0, activeRentSum - bundleDiscount - adminDiscount));
      const bond = Number((useSnap && snap.bond_total != null) ? snap.bond_total : activeChildren.reduce((s, c) => s + Number(c.deposit_amount || 0), 0));
      const deliveryFee = Number((useSnap ? snap.delivery_fee : null) ?? anchor.delivery_fee ?? 0);
      const totalPayable = actualRent + bond + deliveryFee;
      const bondStatus = bondAggregate(children);
      rows.push({
        _kind: "group", _gid: gid, _children: children,
        customer_name: custName, customer_email: custEmail,
        event_date: anchor.event_date, rental_days: anchor.rental_days, return_by_date: anchor.return_by_date,
        booking_status: anchor.booking_status, payment_status: anchor.payment_status, payment_status_manual: anchor.payment_status_manual,
        pricing: { originalRent, bundleDiscount, adminDiscount, actualRent, bond, deliveryFee, totalPayable, bondStatus },
      });
    }

    // Legacy pseudo-groups (frontend-only; no DB linkage).
    for (const { key, bookings } of legacyFinalGroups) {
      const anchor = bookings[0];
      // Only cancelled children are excluded from active revenue.
      const active = bookings.filter(b => !isCancelled(b));
      const activeOriginalRent = active.reduce((s, c) => s + Number(c.rental_cost_base || c.rental_cost || 0), 0);
      const activeAdminDiscount = active.reduce((s, c) => s + Number(c.discount_amount || 0), 0);
      const activeActualRent = Math.max(0, active.reduce((s, c) => s + Math.max(0, Number(c.rental_cost || 0) - Number(c.discount_amount || 0)), 0));
      const activeBond = active.reduce((s, c) => s + Number(c.deposit_amount || 0), 0);
      const deliveryFee = Number(anchor.delivery_fee || 0);
      const totalPayable = activeActualRent + activeBond + deliveryFee;
      const bondStatus = bondAggregate(bookings);
      rows.push({
        _kind: "legacy-group", _gid: key, _children: bookings,
        customer_name: anchor.customer_name, customer_email: anchor.customer_email,
        event_date: anchor.event_date, rental_days: anchor.rental_days, return_by_date: anchor.return_by_date,
        booking_status: anchor.booking_status, payment_status: anchor.payment_status, payment_status_manual: anchor.payment_status_manual,
        pricing: { originalRent: activeOriginalRent, bundleDiscount: 0, adminDiscount: activeAdminDiscount,
                   actualRent: activeActualRent, bond: activeBond, deliveryFee, totalPayable, bondStatus },
      });
    }

    // True singletons.
    for (const b of legacyOrphans) {
      const originalRent = Number(b.rental_cost_base || b.rental_cost || 0);
      const adminDiscount = Number(b.discount_amount || 0);
      const cancelled = isCancelled(b);
      const actualRent = cancelled ? 0 : Math.max(0, Number(b.rental_cost || 0) - adminDiscount);
      const bond = cancelled ? 0 : Number(b.deposit_amount || 0);
      const deliveryFee = Number(b.delivery_fee || 0);
      const totalPayable = actualRent + bond + deliveryFee;
      rows.push({
        _kind: "single", _booking: b,
        customer_name: b.customer_name, customer_email: b.customer_email,
        event_date: b.event_date, rental_days: b.rental_days, return_by_date: b.return_by_date,
        booking_status: b.booking_status, payment_status: b.payment_status, payment_status_manual: b.payment_status_manual,
        pricing: { originalRent, bundleDiscount: 0, adminDiscount, actualRent, bond, deliveryFee, totalPayable, bondStatus: b.bond_status || "—" },
      });
    }
    return rows;
  }, [filteredBookings]);
  const toggleGroup = (gid) => setExpandedGroups(prev => ({ ...prev, [gid]: !prev[gid] }));
  const fmtCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;

  // Phase 5D-post — sort + filter controls (operate on parent rows only).
  const [sortBy, setSortBy] = useState("event_date_desc");
  const [filterKind, setFilterKind] = useState("all");       // all | grouped | single | has_cancelled
  const [filterBondStatus, setFilterBondStatus] = useState("all"); // all | held | refunded | none
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const finalDisplayRows = useMemo(() => {
    let rows = [...displayRows];
    if (filterKind === "grouped")       rows = rows.filter(r => r._kind === "group" || r._kind === "legacy-group");
    else if (filterKind === "single")   rows = rows.filter(r => r._kind === "single");
    else if (filterKind === "has_cancelled") rows = rows.filter(r => (r._children || []).some(c => (c.booking_status || "").toLowerCase() === "cancelled") || (r._booking && (r._booking.booking_status || "").toLowerCase() === "cancelled"));
    if (filterBondStatus !== "all") rows = rows.filter(r => (r.pricing?.bondStatus || "").toLowerCase() === filterBondStatus);
    if (filterFrom) rows = rows.filter(r => r.event_date && r.event_date >= filterFrom);
    if (filterTo)   rows = rows.filter(r => r.event_date && r.event_date <= filterTo);

    const cmp = (a, b) => {
      switch (sortBy) {
        case "customer_asc":    return (a.customer_name || "").localeCompare(b.customer_name || "");
        case "customer_desc":   return (b.customer_name || "").localeCompare(a.customer_name || "");
        case "event_date_asc":  return (a.event_date || "").localeCompare(b.event_date || "");
        case "event_date_desc": return (b.event_date || "").localeCompare(a.event_date || "");
        case "rent_asc":        return (a.pricing?.actualRent || 0) - (b.pricing?.actualRent || 0);
        case "rent_desc":       return (b.pricing?.actualRent || 0) - (a.pricing?.actualRent || 0);
        case "bond_asc":        return (a.pricing?.bond || 0) - (b.pricing?.bond || 0);
        case "bond_desc":       return (b.pricing?.bond || 0) - (a.pricing?.bond || 0);
        case "items_asc":       return ((a._children?.length || 1)) - ((b._children?.length || 1));
        case "items_desc":      return ((b._children?.length || 1)) - ((a._children?.length || 1));
        default: return 0;
      }
    };
    rows.sort(cmp);
    return rows;
  }, [displayRows, sortBy, filterKind, filterBondStatus, filterFrom, filterTo]);


  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />

      <main className="lg:ml-64 p-6 md:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div><h1 className="text-3xl text-[#676664]">Bookings</h1><p className="text-[#676664]/40 text-sm font-light">Manage reservations and security bonds</p></div>
          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#676664]/40" strokeWidth={1.5} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search booking ID, customer, outfit…"
                className="pl-8 w-[260px] border-[#282828]/10"
                data-testid="bookings-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-[160px] border-[#282828]/10"><SelectValue placeholder="Filter" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending_payment">Awaiting Payment</SelectItem><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent></Select>
            <Button onClick={() => setShowManualBooking(true)} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="open-manual-booking-btn"><UserPlus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Manual Booking</Button>
            <Button onClick={fetchBookings} variant="outline" className="border-[#282828]/10" data-testid="refresh-bookings-btn"><RefreshCw className="w-4 h-4" strokeWidth={1.5} /></Button>
          </div>
        </div>

        {fetchError && <AdminApiError message={fetchError} onRetry={fetchBookings} />}

        {/* ── Phase 4 — Outstanding Balances strip ────────────────────── */}
        {outstanding && outstanding.count > 0 && (
          <div className="bg-white border border-l-[3px] border-l-[#B8761C] border-[#282828]/10 p-4 mb-6" data-testid="bookings-outstanding-strip">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-[#B8761C]" strokeWidth={1.5} />
                <div>
                  <div className="text-[12px] text-[#676664] uppercase tracking-wider">Outstanding Balances</div>
                  <div className="text-[20px] text-[#B8761C] font-light leading-tight">${outstanding.total.toFixed(2)} <span className="text-[11px] text-[#676664]/60 ml-1">across {outstanding.count} charge{outstanding.count === 1 ? "" : "s"}</span></div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px] flex-wrap">
                <span className="px-2 py-1 bg-amber-50 border border-amber-200 text-amber-700">Awaiting External: <strong>${outstanding.awaiting_external_total.toFixed(2)}</strong> ({outstanding.awaiting_external_count})</span>
                <span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700">Stripe Sent: <strong>${outstanding.stripe_sent_total.toFixed(2)}</strong> ({outstanding.stripe_sent_count})</span>
              </div>
            </div>
            {outstanding.items.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#282828]/5 divide-y divide-[#282828]/5">
                {outstanding.items.slice(0, 4).map((it) => (
                  <button
                    key={it.charge_id}
                    onClick={() => setSearchQuery(it.booking_id)}
                    className="w-full flex items-center justify-between text-[12px] py-1.5 text-left hover:bg-[#FAF8F0] px-2 -mx-2"
                    data-testid={`outstanding-strip-item-${it.charge_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] text-[#9A7B4F]">{it.booking_id}</span>
                      <span className="text-[#282828]">{it.charge_type_label}</span>
                      <span className="text-[#676664]/50">·</span>
                      <span className="text-[#676664]/70">{it.customer_name || it.customer_email}</span>
                      {it.due_date && <span className="text-[10px] text-[#676664]/50">· Due {it.due_date}</span>}
                    </div>
                    <span className="text-[#B8761C] font-medium">${it.amount.toFixed(2)}</span>
                  </button>
                ))}
                {outstanding.count > 4 && (
                  <div className="pt-2 text-[10px] text-[#676664]/50 text-center">+ {outstanding.count - 4} more · scroll the bookings list below for the full set</div>
                )}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <AdminLoadingState message="Loading bookings..." />
        ) : (filteredBookings || []).length > 0 ? (
          <div className="bg-white border border-[#282828]/10 overflow-hidden">
            {/* Sort + filter control bar (operates on parent rows only) */}
            <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-[#282828]/8 bg-[#FAF8F0]/40 text-[11px]">
              <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Sort</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="border border-[#282828]/15 px-2 py-1 text-[11px] bg-white" data-testid="bookings-sort-by">
                <option value="event_date_desc">Event date · latest</option>
                <option value="event_date_asc">Event date · earliest</option>
                <option value="customer_asc">Customer · A–Z</option>
                <option value="customer_desc">Customer · Z–A</option>
                <option value="rent_desc">Actual rent · high</option>
                <option value="rent_asc">Actual rent · low</option>
                <option value="bond_desc">Bond · high</option>
                <option value="bond_asc">Bond · low</option>
                <option value="items_desc">Items · high</option>
                <option value="items_asc">Items · low</option>
              </select>
              <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium ml-3">Filter</span>
              <select value={filterKind} onChange={e => setFilterKind(e.target.value)}
                className="border border-[#282828]/15 px-2 py-1 text-[11px] bg-white" data-testid="bookings-filter-kind">
                <option value="all">All rows</option>
                <option value="grouped">Grouped only</option>
                <option value="single">Single only</option>
                <option value="has_cancelled">Has cancelled child</option>
              </select>
              <select value={filterBondStatus} onChange={e => setFilterBondStatus(e.target.value)}
                className="border border-[#282828]/15 px-2 py-1 text-[11px] bg-white" data-testid="bookings-filter-bond">
                <option value="all">Bond · any</option>
                <option value="held">Bond · held</option>
                <option value="refunded">Bond · refunded</option>
                <option value="none">Bond · none</option>
              </select>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                className="border border-[#282828]/15 px-2 py-1 text-[11px] bg-white" data-testid="bookings-filter-from" placeholder="From" />
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                className="border border-[#282828]/15 px-2 py-1 text-[11px] bg-white" data-testid="bookings-filter-to" placeholder="To" />
              {(filterKind !== "all" || filterBondStatus !== "all" || filterFrom || filterTo || sortBy !== "event_date_desc") && (
                <button onClick={() => { setSortBy("event_date_desc"); setFilterKind("all"); setFilterBondStatus("all"); setFilterFrom(""); setFilterTo(""); }}
                  className="text-[10px] text-[#9A7B4F] hover:underline ml-auto" data-testid="bookings-filter-reset">Reset</button>
              )}
              <span className="text-[10px] text-[#676664]/60 ml-2" data-testid="bookings-row-count">{finalDisplayRows.length} rows</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAF8F0]">
                  <tr>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase w-8"></th>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Customer</th>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Items</th>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Dates</th>
                    <th className="text-right py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Actual rent</th>
                    <th className="text-right py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Bond</th>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Status</th>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Payment</th>
                    <th className="text-left py-4 px-6 text-[10px] font-medium text-[#676664]/50 tracking-wider uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#6B5B4F]/5">
                  {finalDisplayRows.map((row) => {
                    if (row._kind === "group" || row._kind === "legacy-group") {
                      const gid = row._gid;
                      const isLegacy = row._kind === "legacy-group";
                      const isOpen = !!expandedGroups[gid];
                      const p = row.pricing;
                      const itemsSummary = row._children.map(c => c.outfit_name).slice(0, 3).join(", ") + (row._children.length > 3 ? ` +${row._children.length - 3} more` : "");
                      const cancelledCount = row._children.filter(c => (c.booking_status || "").toLowerCase() === "cancelled").length;
                      return (
                        <React.Fragment key={gid}>
                          <tr className="hover:bg-[#FAF8F0]/50" data-testid={`group-row-${gid}`}>
                            <td className="py-4 px-3">
                              <button onClick={() => toggleGroup(gid)} className="text-[#9A7B4F] hover:bg-[#9A7B4F]/10 p-1" data-testid={`group-toggle-${gid}`}>
                                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                            </td>
                            <td className="py-4 px-6">
                              <div><p className="text-sm text-[#676664]">{row.customer_name}</p><p className="text-[11px] text-[#676664]/40 font-light">{row.customer_email}</p>
                                {isLegacy
                                  ? <p className="text-[9px] text-[#676664]/50 mt-0.5 uppercase tracking-wider">Legacy · matched by email + event date</p>
                                  : <p className="text-[10px] text-[#9A7B4F] mt-0.5 font-mono">{gid}</p>}
                                {(() => {
                                  const owed = row._children.reduce((s, c) => s + (outstandingByBooking[c.id] || 0), 0);
                                  return owed > 0 ? <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200" data-testid={`outstanding-badge-group-${gid}`}>Owes ${owed.toFixed(2)}</span> : null;
                                })()}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="text-[11px]">
                                <p className="text-[#676664]">
                                  <span className="text-[9px] px-1.5 py-0.5 border border-[#9A7B4F]/30 text-[#9A7B4F] bg-[#FAF3E0]/60 uppercase tracking-wider mr-2" data-testid={`group-count-${gid}`}>{row._children.length} items</span>
                                  {cancelledCount > 0 && <span className="text-[9px] px-1.5 py-0.5 border border-red-200 text-red-600 bg-red-50/40 uppercase tracking-wider" data-testid={`group-cancelled-count-${gid}`}>{cancelledCount} cancelled</span>}
                                </p>
                                <p className="text-[10px] text-[#676664]/70 mt-1 max-w-[220px] truncate" title={itemsSummary}>{itemsSummary}</p>
                              </div>
                            </td>
                            <td className="py-4 px-6"><div className="text-[11px]"><p className="text-[#676664]">Event: {row.event_date ? format(new Date(row.event_date + "T00:00:00"), "MMM d") : "N/A"}</p><p className="text-[#676664]/40 font-light">{row.rental_days || 0} day rental</p></div></td>
                            <td className="py-4 px-6 text-right">
                              <div className="text-sm text-[#9A7B4F] font-medium" data-testid={`group-actual-rent-${gid}`}>{fmtCurrency(p.actualRent)}</div>
                              {(p.bundleDiscount > 0 || p.adminDiscount > 0) && (
                                <div className="text-[10px] text-[#676664]/50 line-through" data-testid={`group-original-rent-${gid}`}>{fmtCurrency(p.originalRent)}</div>
                              )}
                            </td>
                            <td className="py-4 px-6 text-right text-sm text-[#676664]" data-testid={`group-bond-${gid}`}>{fmtCurrency(p.bond)}<div className="text-[9px] text-[#676664]/50 uppercase">{p.bondStatus}</div></td>
                            <td className="py-4 px-6"><Badge className={`${statusColors[row.booking_status] || "bg-gray-50 text-gray-600"} border text-[10px]`}>{statusLabels[row.booking_status] || row.booking_status}</Badge></td>
                            <td className="py-4 px-6"><Badge className={`${paymentStatusColors[row.payment_status] || "bg-gray-50 text-gray-600 border-gray-200"} border text-[10px]`}>{paymentStatusLabel(row.payment_status)}</Badge></td>
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-1">
                                <Button variant="outline" size="sm" onClick={() => toggleGroup(gid)} className="border-[#282828]/10" data-testid={`view-group-${gid}`}><Eye className="w-4 h-4" strokeWidth={1.5} /></Button>
                                {!isLegacy && (
                                  <Button variant="outline" size="sm" onClick={() => setEditGroupId(gid)}
                                    className="border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5"
                                    title={`Edit group ${gid}`} data-testid={`edit-group-${gid}`}>
                                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                                  </Button>
                                )}
                                {isLegacy && (
                                  <Button variant="outline" size="sm"
                                    onClick={() => setEditLegacyGroup({ anchorId: row._children[0].id, peerIds: row._children.slice(1).map(c => c.id) })}
                                    className="border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5"
                                    title="Edit legacy group (promotes to a real audited group on save)"
                                    data-testid={`edit-legacy-group-${gid}`}>
                                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-[#FAF8F0]/40" data-testid={`group-expanded-${gid}`}>
                              <td colSpan={9} className="py-3 px-8">
                                <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2">Items in this group</div>
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="text-[#676664]/50 text-[10px] uppercase">
                                      <th className="text-left py-1 pr-4">Product</th>
                                      <th className="text-left py-1 pr-4">Variation</th>
                                      <th className="text-right py-1 pr-4">Base rent</th>
                                      <th className="text-right py-1 pr-4">Applied rent</th>
                                      <th className="text-right py-1 pr-4">Bond</th>
                                      <th className="text-right py-1 pr-4">Booking ID</th>
                                      <th className="text-left py-1">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row._children.map(child => {
                                      const status = (child.booking_status || "").toLowerCase();
                                      const isChildCancelled = status === "cancelled";
                                      const isChildExpired = status === "expired";
                                      const isChildCompleted = status === "completed";
                                      const strike = isChildCancelled; // only cancelled gets strikethrough
                                      const badgeClass = isChildCancelled ? "border-red-200 text-red-600 bg-red-50"
                                        : isChildCompleted ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                                        : isChildExpired ? "border-[#676664]/25 text-[#676664]/70 bg-[#FAF8F0]"
                                        : "border-[#9A7B4F]/25 text-[#9A7B4F] bg-[#FAF3E0]";
                                      return (
                                      <tr key={child.id} className={`border-t border-[#282828]/6 ${isChildCancelled ? "opacity-60 bg-red-50/20" : ""}`}>
                                        <td className="py-1 pr-4 text-[#282828]">
                                          {child.outfit_name}
                                          {status && <span className={`ml-2 text-[9px] px-1 py-0.5 border uppercase ${badgeClass}`} data-testid={`child-status-${child.id}`}>{status}</span>}
                                        </td>
                                        <td className="py-1 pr-4 text-[#676664]/70">{child.rental_option_name || "—"}</td>
                                        <td className="py-1 pr-4 text-right text-[#676664]/70">{fmtCurrency(child.rental_cost_base ?? child.rental_cost)}</td>
                                        <td className="py-1 pr-4 text-right text-[#282828]">
                                          {strike ? <span className="text-[#676664]/50 line-through">{fmtCurrency(child.rental_cost)}</span> : fmtCurrency(child.rental_cost)}
                                          {!isChildCancelled && child.rent_override_amount != null && Number(child.rent_override_amount) !== Number(child.rental_cost_base ?? child.rental_cost) && (
                                            <span className="ml-1 text-[9px] px-1 py-0.5 border border-[#9A7B4F]/40 text-[#9A7B4F] uppercase">override</span>
                                          )}
                                        </td>
                                        <td className="py-1 pr-4 text-right text-[#676664]/70">
                                          {strike ? <span className="line-through">{fmtCurrency(child.deposit_amount)}</span> : fmtCurrency(child.deposit_amount)}
                                          {child.bond_status && <div className="text-[9px] text-[#676664]/50 uppercase">{child.bond_status}</div>}
                                        </td>
                                        <td className="py-1 pr-4 text-right font-mono text-[10px] text-[#9A7B4F]">{child.id}</td>
                                        <td className="py-1">
                                          <div className="flex items-center gap-1">
                                            <Button variant="outline" size="sm" onClick={() => { setSelectedBooking(child); setIsDialogOpen(true); fetchCharges(child.id); }} className="border-[#282828]/10 h-6 px-2 text-[10px]" data-testid={`view-child-${child.id}`}>View</Button>
                                            {isLegacy && (
                                              <Button variant="outline" size="sm" onClick={() => setEditGroupId(child.admin_booking_group_id || child.id)} className="border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5 h-6 px-2 text-[10px]" data-testid={`edit-child-${child.id}`}>Edit</Button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t border-[#282828]/12">
                                      <td colSpan={7} className="pt-2">
                                        <div className="grid grid-cols-4 gap-4 text-[10px]">
                                          <div><span className="text-[#676664]/60">Original rent</span><div className="text-[#282828]">{fmtCurrency(p.originalRent)}</div></div>
                                          {p.bundleDiscount > 0 && <div><span className="text-[#676664]/60">Bundle discount</span><div className="text-emerald-700">− {fmtCurrency(p.bundleDiscount)}</div></div>}
                                          {p.adminDiscount > 0 && <div><span className="text-[#676664]/60">Admin discount</span><div className="text-emerald-700">− {fmtCurrency(p.adminDiscount)}</div></div>}
                                          <div><span className="text-[#676664]/60">Actual rent charged</span><div className="text-[#9A7B4F] font-medium">{fmtCurrency(p.actualRent)}</div></div>
                                          <div><span className="text-[#676664]/60">Bond (refundable)</span><div className="text-[#282828]">{fmtCurrency(p.bond)}</div></div>
                                          {p.deliveryFee > 0 && <div><span className="text-[#676664]/60">Delivery fee</span><div className="text-[#282828]">{fmtCurrency(p.deliveryFee)}</div></div>}
                                          <div><span className="text-[#676664]/60">Total payable</span><div className="text-[#282828] font-medium">{fmtCurrency(p.totalPayable)}</div></div>
                                        </div>
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    }
                    const booking = row._booking;
                    const p = row.pricing;
                    return (
                      <tr key={booking.id} className="hover:bg-[#FAF8F0]/50">
                        <td className="py-4 px-3"></td>
                        <td className="py-4 px-6"><div><p className="text-sm text-[#676664]">{booking.customer_name}</p><p className="text-[11px] text-[#676664]/40 font-light">{booking.customer_email}</p><OutstandingBadge bookingId={booking.id} /></div></td>
                        <td className="py-4 px-6 text-sm text-[#676664]">{booking.outfit_name}<div className="text-[10px] text-[#676664]/60">{booking.rental_option_name || ""}</div></td>
                        <td className="py-4 px-6"><div className="text-[11px]"><p className="text-[#676664]">Event: {booking.event_date ? format(new Date(booking.event_date + "T00:00:00"), "MMM d") : "N/A"}</p><p className="text-[#676664]/40 font-light">Return by: {booking.return_by_date ? format(new Date(booking.return_by_date + "T00:00:00"), "MMM d") : "N/A"}</p></div></td>
                        <td className="py-4 px-6 text-right">
                          <div className="text-sm text-[#9A7B4F] font-medium" data-testid={`single-actual-rent-${booking.id}`}>{fmtCurrency(p.actualRent)}</div>
                          {p.adminDiscount > 0 && <div className="text-[10px] text-[#676664]/50 line-through">{fmtCurrency(p.originalRent)}</div>}
                        </td>
                        <td className="py-4 px-6 text-right text-sm text-[#676664]" data-testid={`single-bond-${booking.id}`}>{fmtCurrency(p.bond)}<div className="text-[9px] text-[#676664]/50 uppercase">{p.bondStatus}</div></td>
                        <td className="py-4 px-6"><Badge className={`${statusColors[booking.booking_status]} border text-[10px]`}>{statusLabels[booking.booking_status] || booking.booking_status}</Badge></td>
                        <td className="py-4 px-6"><Badge className={`${paymentStatusColors[booking.payment_status] || "bg-gray-50 text-gray-600 border-gray-200"} border text-[10px]`}>{paymentStatusLabel(booking.payment_status)}</Badge></td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm" onClick={() => { setSelectedBooking(booking); setIsDialogOpen(true); fetchCharges(booking.id); }} className="border-[#282828]/10" data-testid={`view-booking-${booking.id}`}><Eye className="w-4 h-4" strokeWidth={1.5} /></Button>
                            <Button variant="outline" size="sm"
                              onClick={() => setEditGroupId(booking.admin_booking_group_id || booking.id)}
                              className="border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5"
                              title={`Edit booking ${booking.id}`}
                              data-testid={`edit-booking-${booking.id}`}>
                              <Pencil className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-24 bg-white border border-[#282828]/10">
            <CalendarDays className="w-16 h-16 text-[#676664]/10 mx-auto mb-4" strokeWidth={1} />
            <h3 className="text-xl text-[#676664] mb-2">No bookings found</h3>
            <p className="text-[#676664]/40 font-light text-sm">{statusFilter !== "all" ? "Try different filter" : "Bookings will appear here"}</p>
          </div>
        )}
      </main>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#676664] flex items-center gap-2 flex-wrap">
              <span>Booking Details</span>
              {selectedBooking && (
                <>
                  <span className="font-mono text-[12px] text-[#676664]/70 bg-[#FAF8F0] px-2 py-0.5 border border-[#282828]/8" data-testid="booking-id-display">{selectedBooking.id}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyBookingId(selectedBooking.id)}
                    className="h-7 px-2 text-[10px] tracking-wider uppercase border-[#282828]/10 text-[#676664]"
                    data-testid="copy-booking-id-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" strokeWidth={1.5} /> Copy ID
                  </Button>
                  {selectedBooking.source === "admin_manual" && (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[9px]">MANUAL</Badge>
                  )}
                  <SettlementBadge status={selectedBooking.settlement_state} />
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <>
              {/* Tab switcher */}
              <div className="flex border-b border-[#282828]/8 mt-2 overflow-x-auto" data-testid="booking-tabs">
                {[
                  { key: "details",  label: "Details",   icon: Eye },
                  { key: "products", label: "Products",  icon: ArrowRightLeft },
                  { key: "timeline", label: "Timeline",  icon: History },
                  { key: "activity", label: "Activity",  icon: History },
                  { key: "notes",    label: "Notes",     icon: StickyNote },
                  { key: "settlement", label: "Settlement", icon: Scale },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveBookingTab(t.key)}
                    className={`px-4 py-2.5 text-[11px] tracking-wider uppercase font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                      activeBookingTab === t.key
                        ? "border-[#9A7B4F] text-[#282828]"
                        : "border-transparent text-[#676664]/60 hover:text-[#282828]"
                    }`}
                    data-testid={`booking-tab-${t.key}`}
                  >
                    <t.icon className="w-3 h-3" /> {t.label}
                  </button>
                ))}
              </div>

              {activeBookingTab === "products" && (
                <div className="mt-5 space-y-4" data-testid="products-tab">
                  <div className="bg-[#FAF8F0] p-4 border border-[#282828]/8">
                    <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2">Current outfit</div>
                    <div className="text-[14px] text-[#282828]">{selectedBooking.outfit_name}</div>
                    <div className="text-[11px] text-[#676664]/60 mt-1">
                      Rental ${(selectedBooking.rental_cost || 0).toFixed(2)} · Bond ${(selectedBooking.deposit_amount || 0).toFixed(2)} · Total <strong>${(selectedBooking.total_amount || 0).toFixed(2)}</strong>
                    </div>
                  </div>
                  <Button
                    onClick={() => setIsExchangeOpen(true)}
                    className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
                    data-testid="open-exchange-btn"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" /> Exchange / Change Products
                  </Button>
                  {selectedBooking.exchange_history && selectedBooking.exchange_history.length > 0 && (
                    <div className="text-[11px] text-[#676664]/70 mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Previous exchanges</div>
                      {selectedBooking.exchange_history.map((h) => (
                        <div key={h.id} className="border-l-2 border-[#9A7B4F]/30 pl-2 mb-1.5">
                          {h.from_outfit_name} → <strong>{h.to_outfit_name}</strong>
                          <div className="text-[10px] text-[#676664]/50">Δ ${h.price_delta?.toFixed(2)} · {h.payment_action} · by {h.admin_email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedBooking.payment_adjustments && selectedBooking.payment_adjustments.length > 0 && (
                    <div className="text-[11px] text-[#676664]/70 mt-3 pt-3 border-t border-[#282828]/8">
                      <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Payment adjustments</div>
                      {selectedBooking.payment_adjustments.slice().reverse().slice(0, 5).map((a) => (
                        <div key={a.id} className="bg-amber-50/60 border border-amber-100 px-2 py-1.5 mb-1">
                          <span className="font-mono text-[10px]">{a.action}</span> · Δ ${a.amount_delta?.toFixed(2)}
                          {a.pending_amount > 0 && <> · pending ${a.pending_amount?.toFixed(2)}</>}
                          {a.note && <div className="text-[10px] italic mt-0.5">"{a.note}"</div>}
                          <div className="text-[9px] text-[#676664]/50">by {a.admin_email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeBookingTab === "timeline" && (
                <div className="mt-5" data-testid="booking-timeline-tab">
                  <Timeline
                    bookingId={selectedBooking.id}
                    customerId={selectedBooking.customer_id}
                    title="Communication & Activity Timeline"
                  />
                </div>
              )}

              {activeBookingTab === "activity" && (
                <div className="mt-5" data-testid="activity-tab">
                  <BookingActivityTimeline bookingId={selectedBooking.id} />
                </div>
              )}

              {activeBookingTab === "settlement" && (
                <SettlementPanel booking={selectedBooking} onChanged={async () => {
                  const id = selectedBooking.id;
                  const fresh = (await fetchBookings())?.find((x) => x.id === id);
                  if (fresh) setSelectedBooking((prev) => (prev?.id === id ? { ...prev, ...fresh } : prev));
                  fetchCharges(id);
                  fetchOutstanding();
                }} />
              )}

              {activeBookingTab === "notes" && (
                <div className="mt-5" data-testid="notes-tab">
                  <BookingNotesPanel booking={selectedBooking} onChanged={fetchBookings} />
                </div>
              )}

              {activeBookingTab === "details" && (
            <div className="space-y-6 mt-4">
              <div className="bg-[#FAF8F0] p-4">
                <h3 className="text-[11px] text-[#676664] mb-3 tracking-wide uppercase">Customer</h3>
                <div className="space-y-2 text-sm font-light">
                  <p><span className="text-[#676664]/50">Name:</span> <span className="text-[#676664]">{selectedBooking.customer_name}</span></p>
                  <p><span className="text-[#676664]/50">Email:</span> {selectedBooking.customer_email}</p>
                  <p><span className="text-[#676664]/50">Phone:</span> {selectedBooking.customer_phone}</p>
                  {selectedBooking.customer_phone && (
                    <Button variant="outline" size="sm"
                      onClick={() => setSmsModal({ to_phone: selectedBooking.customer_phone, customer_name: selectedBooking.customer_name, customer_id: selectedBooking.customer_id, booking_id: selectedBooking.id })}
                      className="h-7 px-2 text-[10px] tracking-wider uppercase border-[#9A7B4F]/30 text-[#9A7B4F] mt-1"
                      data-testid="booking-send-sms-btn">
                      <MessageSquare className="w-3 h-3 mr-1" strokeWidth={1.5} /> Send SMS
                    </Button>
                  )}
                </div>
                {/* Patch B.1 — Full-featured Trial Lead linkage section */}
                <div className="mt-4">
                  <TrialLeadPicker
                    bookingId={selectedBooking.id}
                    linkedLeadId={selectedBooking.trial_lead_id || ""}
                    onLinkageChanged={(newLeadId) => {
                      // Update the currently open booking + the list so both
                      // the modal and the underlying row stay in sync without
                      // requiring a full refetch.
                      setSelectedBooking((prev) => prev ? { ...prev, trial_lead_id: newLeadId } : prev);
                      setBookings((prev) => prev.map((b) =>
                        b.id === selectedBooking.id ? { ...b, trial_lead_id: newLeadId } : b));
                    }}
                    onOpenLead={(leadId) => {
                      setSelectedBooking(null);
                      navigate(`/admin/trial-leads?lead=${leadId}`);
                    }}
                  />
                </div>
              </div>

              {/* ── Phase 1 — Payment Summary ─────────────────────────── */}
              <div className="bg-white border border-[#282828]/10 border-l-[3px] border-l-[#0E7C5A] p-4" data-testid="payment-summary-panel">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[11px] text-[#676664] tracking-wide uppercase flex items-center gap-2">
                    <DollarSign className="w-3 h-3 text-[#9A7B4F]" strokeWidth={1.5} />
                    Payment Summary
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPaymentSummary(selectedBooking)}
                    className="h-7 px-2 text-[10px] tracking-wider uppercase border-[#282828]/10 text-[#676664]"
                    data-testid="edit-payment-summary-btn"
                  >
                    <Pencil className="w-3 h-3 mr-1" strokeWidth={1.5} /> Edit
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[12px]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2 font-medium">Rental Payment</div>
                    <div className="flex justify-between py-1.5 border-b border-[#282828]/5"><span className="text-[#676664]/50">Method</span><span className="text-[#282828]">{_labelOf(RENTAL_METHOD_OPTIONS, selectedBooking.rental_payment_method)}</span></div>
                    <div className="flex justify-between py-1.5 border-b border-[#282828]/5"><span className="text-[#676664]/50">Status</span><Badge className={`${rentalStatusBadgeColor(selectedBooking.rental_payment_status_v2 || (selectedBooking.payment_status === "paid" ? "paid" : selectedBooking.payment_status === "partial" ? "partially_paid" : "unpaid"))} border text-[9px]`}>{_labelOf(RENTAL_STATUS_OPTIONS, selectedBooking.rental_payment_status_v2 || (selectedBooking.payment_status === "paid" ? "paid" : selectedBooking.payment_status === "partial" ? "partially_paid" : "unpaid"))}</Badge></div>
                    <div className="flex justify-between py-1.5 border-b border-[#282828]/5"><span className="text-[#676664]/50">Paid</span><span className="text-[#282828]">{selectedBooking.rental_amount_paid != null ? `$${(selectedBooking.rental_amount_paid).toFixed(2)} / $${(selectedBooking.rental_cost || 0).toFixed(2)}` : `$${(selectedBooking.rental_cost || 0).toFixed(2)}`}</span></div>
                    <div className="flex justify-between py-1.5"><span className="text-[#676664]/50">Recorded</span><span className="text-[#282828]">{selectedBooking.rental_paid_at ? format(new Date(selectedBooking.rental_paid_at.length > 10 ? selectedBooking.rental_paid_at : selectedBooking.rental_paid_at + "T00:00:00"), "d MMM yyyy") : "—"}</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2 font-medium">Bond / Deposit</div>
                    <div className="flex justify-between py-1.5 border-b border-[#282828]/5"><span className="text-[#676664]/50">Method</span><span className="text-[#282828]">{_labelOf(BOND_METHOD_OPTIONS, selectedBooking.bond_collection_method)}</span></div>
                    <div className="flex justify-between py-1.5 border-b border-[#282828]/5"><span className="text-[#676664]/50">Status</span><Badge className={`${bondStatusBadgeColor(selectedBooking.bond_status_v2 || (selectedBooking.bond_status === "refunded" ? "refunded" : "collected"))} border text-[9px]`}>{_labelOf(BOND_STATUS_V2_OPTIONS, selectedBooking.bond_status_v2 || (selectedBooking.bond_status === "refunded" ? "refunded" : selectedBooking.bond_status === "retained" ? "fully_deducted" : "collected"))}</Badge></div>
                    <div className="flex justify-between py-1.5 border-b border-[#282828]/5"><span className="text-[#676664]/50">Amount</span><span className="text-[#282828]">${(selectedBooking.deposit_amount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between py-1.5"><span className="text-[#676664]/50">Collected</span><span className="text-[#282828]">{selectedBooking.bond_collected_at ? format(new Date(selectedBooking.bond_collected_at.length > 10 ? selectedBooking.bond_collected_at : selectedBooking.bond_collected_at + "T00:00:00"), "d MMM yyyy") : "—"}</span></div>
                  </div>
                </div>
                {selectedBooking.internal_payment_notes && (
                  <div className="mt-3 pt-3 border-t border-[#282828]/5 text-[11px] text-[#676664]/80 whitespace-pre-line"><span className="text-[#676664]/50">Internal note: </span>{selectedBooking.internal_payment_notes}</div>
                )}

                {/* ── Phase-2 — Stripe Fee read-only display ── */}
                {selectedBooking.stripe_payment_intent_id && (
                  <div className="mt-3 pt-3 border-t border-[#282828]/5" data-testid="stripe-fee-panel">
                    <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2 font-medium flex items-center gap-2">
                      Stripe Fee
                      {selectedBooking.stripe_fee_status === "captured" && (
                        <span className="px-1.5 py-0.5 bg-[#0E7C5A]/10 text-[#0E7C5A] text-[9px] tracking-wider" data-testid="stripe-fee-badge">CAPTURED</span>
                      )}
                      {selectedBooking.stripe_fee_status === "pending" && (
                        <span className="px-1.5 py-0.5 bg-[#B8761C]/10 text-[#B8761C] text-[9px] tracking-wider" data-testid="stripe-fee-badge">PENDING</span>
                      )}
                      {selectedBooking.stripe_fee_status === "unavailable" && (
                        <span className="px-1.5 py-0.5 bg-[#676664]/10 text-[#676664] text-[9px] tracking-wider" data-testid="stripe-fee-badge">UNAVAILABLE</span>
                      )}
                      {selectedBooking.stripe_fee_status === "error" && (
                        <span className="px-1.5 py-0.5 bg-[#B23A48]/10 text-[#B23A48] text-[9px] tracking-wider" data-testid="stripe-fee-badge">ERROR</span>
                      )}
                      {!selectedBooking.stripe_fee_status && (
                        <span className="px-1.5 py-0.5 bg-[#676664]/10 text-[#676664]/60 text-[9px] tracking-wider" data-testid="stripe-fee-badge">NOT CAPTURED</span>
                      )}
                    </div>
                    <div className="text-[12px] space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-[#676664]/50">Fee charged</span>
                        <span className="text-[#282828]">
                          {selectedBooking.stripe_fee_status === "captured" && selectedBooking.stripe_fee_amount != null
                            ? `$${Number(selectedBooking.stripe_fee_amount).toFixed(2)} ${(selectedBooking.stripe_fee_currency || "aud").toUpperCase()}`
                            : "—"}
                        </span>
                      </div>
                      {selectedBooking.stripe_balance_transaction_id && (
                        <div className="flex justify-between">
                          <span className="text-[#676664]/50">Balance txn</span>
                          <span className="text-[10px] text-[#676664]/70 font-mono">{selectedBooking.stripe_balance_transaction_id}</span>
                        </div>
                      )}
                      {selectedBooking.stripe_fee_captured_at && (
                        <div className="flex justify-between">
                          <span className="text-[#676664]/50">Captured at</span>
                          <span className="text-[10px] text-[#676664]/70">{format(new Date(selectedBooking.stripe_fee_captured_at), "d MMM yyyy HH:mm")}</span>
                        </div>
                      )}
                      {selectedBooking.stripe_fee_error && (
                        <div className="text-[10px] text-[#B23A48]/80 italic mt-1">{selectedBooking.stripe_fee_error}</div>
                      )}
                    </div>
                  </div>
                )}
                {/* ── /Phase-2 ── */}
                {/* ── Bond Activity sub-card (visible whenever a refund/deduction has been recorded) ── */}
                {(settledViaWorkflow(selectedBooking) || selectedBooking.bond_refund_amount > 0 || selectedBooking.bond_deduction_amount > 0 || ["refunded","fully_deducted","partially_deducted"].includes(selectedBooking.bond_status_v2)) && (
                  <div className="mt-3 pt-3 border-t border-[#282828]/5">
                    <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2 font-medium">Bond Activity</div>
                    <div className="text-[12px] text-[#282828] space-y-1.5">
                      {settledViaWorkflow(selectedBooking) ? <SettledBondLine booking={selectedBooking} /> : (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        {selectedBooking.bond_refund_amount > 0 && (
                          <span><strong className="text-[#0E7C5A]">${Number(selectedBooking.bond_refund_amount).toFixed(2)} refunded</strong></span>
                        )}
                        {selectedBooking.bond_deduction_amount > 0 && (
                          <span className="text-[#676664]">·</span>
                        )}
                        {selectedBooking.bond_deduction_amount > 0 && (
                          <span><strong className="text-[#B23A48]">${Number(selectedBooking.bond_deduction_amount).toFixed(2)} retained</strong></span>
                        )}
                        {selectedBooking.bond_refund_date && (
                          <span className="text-[#676664]">· {format(new Date(selectedBooking.bond_refund_date + (selectedBooking.bond_refund_date.length > 10 ? "" : "T00:00:00")), "d MMM yyyy")}</span>
                        )}
                      </div>
                      )}
                      <div className="text-[11px] text-[#676664]/80 flex flex-wrap gap-x-3 gap-y-0.5">
                        {selectedBooking.bond_refund_method && (
                          <span><span className="text-[#676664]/50">Method:</span> {_labelOf(BOND_METHOD_OPTIONS, selectedBooking.bond_refund_method)}</span>
                        )}
                        <span><span className="text-[#676664]/50">Status:</span> {_labelOf(BOND_STATUS_V2_OPTIONS, selectedBooking.bond_status_v2)}</span>
                        {selectedBooking.bond_refunded_externally && (
                          <span className="text-[#676664]/60">· Processed externally (no Stripe)</span>
                        )}
                      </div>
                      {selectedBooking.bond_refund_reason && (
                        <div className="text-[11px] text-[#676664]/80"><span className="text-[#676664]/50">Reason:</span> {selectedBooking.bond_refund_reason}</div>
                      )}
                      {selectedBooking.bond_refund_customer_notes && (
                        <div className="text-[11px] text-[#676664]/80 italic"><span className="text-[#676664]/50 not-italic">Customer note:</span> {selectedBooking.bond_refund_customer_notes}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* ── /Phase 1 ──────────────────────────────────────────── */}

              {/* Email delivery status */}
              <div className="bg-[#FAF8F0] p-4 space-y-1">
                <h3 className="text-[11px] text-[#676664] mb-2 tracking-wide uppercase">Emails</h3>
                <EmailStatusBadge entityId={selectedBooking.id} emailType="booking_confirmation" label="Booking confirmation → customer" compact />
              </div>
              <div className="bg-[#FAF8F0] p-4">
                <h3 className="text-[11px] text-[#676664] mb-3 tracking-wide uppercase flex items-center gap-2">
                  {selectedBooking.delivery_method === "pickup" ? <MapPin className="w-3 h-3 text-[#9A7B4F]" /> : <Truck className="w-3 h-3 text-[#9A7B4F]" />}
                  {selectedBooking.delivery_method === "pickup" ? "Local Pickup" : "Local Delivery"}
                </h3>
                {selectedBooking.delivery_address && <p className="text-sm text-[#676664]/60 font-light">{selectedBooking.delivery_address}</p>}
                {selectedBooking.postcode && <p className="text-sm text-[#676664]/60 font-light">Postcode: {selectedBooking.postcode}</p>}
                {(() => {
                  // Section 5 — flag pickup-only bookings so admin knows delivery wasn't an option.
                  if (!selectedBooking.event_date || selectedBooking.delivery_method !== "pickup") return null;
                  try {
                    const [y, m, d] = String(selectedBooking.event_date).slice(0, 10).split("-").map(Number);
                    const nowMel = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
                    const today = new Date(nowMel.getFullYear(), nowMel.getMonth(), nowMel.getDate());
                    const ev = new Date(y, m - 1, d);
                    const delta = Math.round((ev - today) / (1000 * 60 * 60 * 24));
                    if (delta < 0 || delta > 1) return null;
                    return (
                      <p className="text-[10px] text-[#9A7B4F] font-medium mt-2 uppercase tracking-[0.12em]" data-testid="admin-booking-pickup-only-badge">
                        Pickup only · same-/next-day booking
                      </p>
                    );
                  } catch { return null; }
                })()}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Piece</span><span className="text-[#676664]">{selectedBooking.outfit_name}</span></div>
                <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Event Date</span><span>{selectedBooking.event_date ? format(new Date(selectedBooking.event_date + "T00:00:00"), "EEE, MMM d, yyyy") : "N/A"}</span></div>
                <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Delivery Date</span><span>{selectedBooking.delivery_date ? format(new Date(selectedBooking.delivery_date + "T00:00:00"), "EEE, MMM d") : "N/A"}</span></div>
                <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Return By</span><span className="text-[#C4A4A0] font-medium">{selectedBooking.return_by_date ? format(new Date(selectedBooking.return_by_date + "T00:00:00"), "EEE, MMM d") : "N/A"}</span></div>
                <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Rental</span><span>${selectedBooking.rental_cost.toFixed(2)}</span></div>
                <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Deposit / Bond</span><span>${selectedBooking.deposit_amount.toFixed(2)}{selectedBooking.bond_type === "percentage" ? ` (${selectedBooking.bond_type})` : ""}</span></div>
                {selectedBooking.bond_status && selectedBooking.bond_status !== "held" && (
                  <div className="flex justify-between py-2 border-b border-[#282828]/5 bg-purple-50 -mx-2 px-2">
                    <span className="text-purple-600 font-light">Bond: {selectedBooking.bond_status.replace("_", " ")}</span>
                    <span className="text-purple-600">{selectedBooking.bond_refund_amount != null ? `$${selectedBooking.bond_refund_amount.toFixed(2)} refunded` : ""}{settledViaWorkflow(selectedBooking) && Number(selectedBooking.bond_charges_deducted_total) > 0 ? ` · $${Number(selectedBooking.bond_charges_deducted_total).toFixed(2)} used` : ""}</span>
                  </div>
                )}
                {selectedBooking.bond_refund_reason && (
                  <div className="text-[11px] text-[#676664]/60 font-light italic px-1">Reason: {selectedBooking.bond_refund_reason}</div>
                )}
                {selectedBooking.promotion_discount > 0 && (
                  <div className="flex justify-between py-2 border-b border-[#282828]/5 text-green-600"><span className="font-light">Promo Discount</span><span>-${selectedBooking.promotion_discount.toFixed(2)}</span></div>
                )}
                {selectedBooking.delivery_fee > 0 && (
                  <div className="flex justify-between py-2 border-b border-[#282828]/5"><span className="text-[#676664]/50 font-light">Delivery Fee</span><span>${selectedBooking.delivery_fee.toFixed(2)}</span></div>
                )}
                {selectedBooking.late_fee > 0 && (() => {
                  const linked = charges.find((c) => c.id === selectedBooking.late_fee_ledger_charge_id);
                  return (
                    <div className="flex justify-between py-2 border-b border-[#282828]/5 bg-red-50 -mx-2 px-2" data-testid="booking-late-fee-line">
                      <span className="text-red-600 font-light">Late Fee{linked ? " (ledger)" : ""}{linked?.status === "paid" ? (linked.payment_method === "bond_deduction" ? " · paid from bond" : " · paid") : ""}</span>
                      <span className="text-red-600 text-right">
                        ${Number(linked && linked.status !== "cancelled" ? linked.amount : (linked ? 0 : selectedBooking.late_fee)).toFixed(2)}
                        {linked && Number(linked.amount) !== Number(selectedBooking.late_fee) && <span className="block text-[10px] text-[#676664]/60">original record ${selectedBooking.late_fee.toFixed(2)}</span>}
                      </span>
                    </div>
                  );
                })()}
                <div className="flex justify-between py-2"><span className="text-[#676664]">Total</span><span className="text-[#9A7B4F]">${selectedBooking.total_amount.toFixed(2)}</span></div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#676664]/50 text-sm font-light">Status</span>
                <Select value={selectedBooking.booking_status} onValueChange={(v) => handleStatusChange(selectedBooking.id, v)} disabled={processing}>
                  <SelectTrigger className="w-[140px] border-[#282828]/10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#676664]/50 text-sm font-light">Payment</span>
                <Badge className={`${paymentStatusColors[selectedBooking.payment_status] || "bg-gray-50 text-gray-600 border-gray-200"} border`}>{paymentStatusLabel(selectedBooking.payment_status)}</Badge>
              </div>
              {((isPaymentUnlocked(selectedBooking) && selectedBooking.booking_status !== "completed") || selectedBooking.bond_closed === false) && (
                <div className="space-y-3">
                  {selectedBooking.booking_status !== "completed" && (
                  <Button 
                    onClick={() => { setReturnDate(format(new Date(), "yyyy-MM-dd")); setIsReturnDialogOpen(true); }} 
                    className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" 
                    data-testid="record-return-btn"
                  >
                    <Clock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Record Return
                  </Button>
                  )}
                  <Button onClick={() => openBondRefund(selectedBooking)} disabled={processing} variant="outline" className="w-full border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/10" data-testid="refund-deposit-btn">
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                    Refund Bond (${refundableBond(selectedBooking).toFixed(2)})
                  </Button>
                </div>
              )}

              {/* Quick Actions Grid */}
              {isPaymentUnlocked(selectedBooking) && (
                <div className="border border-[#282828]/10 p-4 space-y-3">
                  <h3 className="text-[11px] text-[#676664] tracking-wide uppercase flex items-center gap-2">
                    <Wrench className="w-3 h-3 text-[#9A7B4F]" />
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <Button variant="outline" size="sm" onClick={() => openCreateCharge("late_fee")} className="border-[#282828]/10 text-[#676664] hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-200 text-[11px] h-9" data-testid="charge-late-fee-btn">
                      <Banknote className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Late Fee
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openCreateCharge("damage")} className="border-[#282828]/10 text-[#676664] hover:bg-red-50 hover:text-red-700 hover:border-red-200 text-[11px] h-9" data-testid="charge-damage-btn">
                      <Wrench className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Damage
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openCreateCharge("cleaning")} className="border-[#282828]/10 text-[#676664] hover:bg-cyan-50 hover:text-cyan-700 hover:border-cyan-200 text-[11px] h-9" data-testid="charge-cleaning-btn">
                      <Wrench className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Cleaning
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openCreateCharge("missing")} className="border-[#282828]/10 text-[#676664] hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 text-[11px] h-9" data-testid="charge-missing-btn">
                      <AlertTriangle className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Missing
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openInspectionDialog(selectedBooking)} className="border-[#282828]/10 text-[#676664] hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 text-[11px] h-9" data-testid="inspection-photos-btn">
                      <Camera className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Inspection
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDamageDialog(selectedBooking)} className="border-[#282828]/10 text-[#676664] hover:bg-[#FAF8F0] text-[11px] h-9" data-testid="assess-condition-btn">
                      <Shield className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Condition
                    </Button>
                  </div>
                  {selectedBooking.inspection_status && (
                    <div className="text-[11px] text-[#676664]/50 flex items-center gap-1.5">
                      <Camera className="w-3 h-3" />
                      Last inspection: <span className="text-[#9A7B4F]">{selectedBooking.inspection_status}</span>
                      {selectedBooking.inspection_date && <span>({format(new Date(selectedBooking.inspection_date), "MMM d")})</span>}
                    </div>
                  )}
                </div>
              )}

              {/* ── Phase 3 — Charges list ─────────────────────────────── */}
              {(charges.length > 0 || chargesLoading) && (
                <div className="border border-[#282828]/10 p-4 space-y-2" data-testid="charges-list-card">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] text-[#676664] tracking-wide uppercase flex items-center gap-2">
                      <Banknote className="w-3 h-3 text-[#9A7B4F]" />
                      Charges <span className="text-[#676664]/40 normal-case font-normal">({charges.length})</span>
                    </h3>
                    {isPaymentUnlocked(selectedBooking) && (
                      <Button variant="outline" size="sm" onClick={() => openCreateCharge("other")} className="h-7 px-2 text-[10px] tracking-wider uppercase border-[#282828]/10 text-[#676664]" data-testid="charge-add-other-btn">
                        + New
                      </Button>
                    )}
                  </div>
                  {chargesMeta?.outstanding_count > 0 && (
                    <div className="text-[11px] px-2 py-1.5 bg-amber-50 border border-amber-200 text-amber-800" data-testid="booking-outstanding-banner">
                      Outstanding: <strong>${Number(chargesMeta.outstanding_total).toFixed(2)}</strong> across {chargesMeta.outstanding_count} charge{chargesMeta.outstanding_count === 1 ? "" : "s"}
                    </div>
                  )}
                  {chargesMeta?.bond_summary?.deducted_by_charges > 0 && (
                    <div className="text-[11px] text-[#676664]/70" data-testid="booking-bond-deducted-note">
                      ${Number(chargesMeta.bond_summary.deducted_by_charges).toFixed(2)} deducted from bond for charges · refundable bond ${Number(chargesMeta.bond_summary.available).toFixed(2)}
                    </div>
                  )}
                  <div className="divide-y divide-[#282828]/5">
                    {charges.map((c) => (
                      <div key={c.id} className={`py-2 flex flex-wrap items-start justify-between gap-3 ${c.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`charge-row-${c.id}`}>
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] font-medium text-[#282828]">{c.charge_type_label || c.charge_type}</span>
                            <span className="text-[12px] text-[#9A7B4F] font-semibold">${Number(c.amount).toFixed(2)}</span>
                            <Badge className={`${chargeStatusBadge(c.status)} border text-[9px]`}>{chargeStatusLabel(c.status)}</Badge>
                            {c.payment_method === "bond_deduction" && <Badge className="bg-[#9A7B4F]/10 text-[#9A7B4F] border-[#9A7B4F]/30 border text-[9px]" data-testid={`charge-bond-badge-${c.id}`}>From bond</Badge>}
                            {c.split_group_id && <Badge className="bg-white text-[#676664] border-[#282828]/15 border text-[9px]" data-testid={`charge-split-badge-${c.id}`}>Split · {c.split_role === "bond" ? "bond part" : "remainder"} of ${Number(c.split_total || 0).toFixed(2)}</Badge>}
                            <ChargeSettlementBadges c={c} />
                            {c.is_legacy_migrated && !adoptedFromHistory(c) && <Badge className="bg-white text-[#676664] border-dashed border-[#282828]/25 border text-[9px]" data-testid={`charge-legacy-badge-${c.id}`}>Historical{c.legacy_reviewed ? " · corrected" : ""}</Badge>}
                          </div>
                          {c.description && <div className="text-[11px] text-[#676664]/70 mt-0.5">{c.description}</div>}
                          <div className="text-[10px] text-[#676664]/50 mt-1 space-x-2">
                            {c.collection_mode && COLLECTION_MODE_LABELS[c.collection_mode] && <span>{COLLECTION_MODE_LABELS[c.collection_mode]}</span>}
                            {c.payment_method && c.payment_method !== "bond_deduction" && <span>· {c.payment_method.replace(/_/g, " ")}</span>}
                            {c.payment_date && <span>· {c.payment_date}</span>}
                            {c.stripe_payment_url && c.status === "stripe_invoice_sent" && <a href={c.stripe_payment_url} target="_blank" rel="noopener noreferrer" className="text-[#9A7B4F] underline">Stripe Link ↗</a>}
                          </div>
                        </div>
                        {c.settlement_locked ? (
                          <span className="text-[10px] text-[#676664]/70 self-center" data-testid={`charge-locked-note-${c.id}`}>Change via Amend / Settle</span>
                        ) : (<>
                        {["awaiting_external_payment", "needs_review"].includes(c.status) && (
                          <Button variant="outline" size="sm" onClick={() => markChargePaidInline(c.id)} className="h-7 px-2 text-[10px] tracking-wider uppercase border-[#0E7C5A]/30 text-[#0E7C5A]" data-testid={`charge-mark-paid-${c.id}`}>
                            Mark Paid
                          </Button>
                        )}
                        {c.status !== "cancelled" && (c.is_legacy_migrated || !(c.status === "paid" && (c.stripe_session_id || c.stripe_payment_intent_id))) && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => editChargeInline(c)} className="h-7 px-2 text-[10px] tracking-wider uppercase border-[#282828]/12 text-[#676664]" data-testid={`charge-edit-${c.id}`}>
                              {c.is_legacy_migrated ? "Correct" : "Edit"}
                            </Button>
                            {!c.is_legacy_migrated && (
                              <Button variant="outline" size="sm" onClick={() => deleteChargeInline(c)} className="h-7 px-2 text-[10px] tracking-wider uppercase border-red-200 text-red-500 hover:bg-red-50" data-testid={`charge-delete-${c.id}`}>
                                Delete
                              </Button>
                            )}
                          </>
                        )}
                        </>)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* ── /Phase 3 ───────────────────────────────────────────── */}
              {selectedBooking.actual_return_date && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-700">
                    <span className="font-medium">Returned:</span> {format(new Date(selectedBooking.actual_return_date + "T00:00:00"), "EEE, MMM d, yyyy")}
                    {(selectedBooking.late_fee > 0 || selectedBooking.late_fee_assessed > 0) && (
                      <span className="block text-red-600 mt-1">Late fee of ${Number(selectedBooking.late_fee || selectedBooking.late_fee_assessed).toFixed(2)} {selectedBooking.late_fee > 0 ? "applied" : "assessed — see Charges"}</span>
                    )}
                  </p>
                </div>
              )}

              {/* Damage Assessment Section */}
              {isPaymentUnlocked(selectedBooking) && (
                <div className="border border-[#282828]/10 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] text-[#676664] tracking-wide uppercase flex items-center gap-2">
                      <Shield className="w-3 h-3 text-[#9A7B4F]" />
                      Damage Assessment
                    </h3>
                    {selectedBooking.damage_status && selectedBooking.damage_status !== "normal" && (
                      <Badge className={`${damageStatusColors[selectedBooking.damage_status] || ""} border text-[10px]`}>
                        {damageStatusLabels[selectedBooking.damage_status] || selectedBooking.damage_status}
                      </Badge>
                    )}
                  </div>
                  
                  {selectedBooking.damage_status && selectedBooking.damage_status !== "normal" && (
                    <div className="text-[12px] space-y-1">
                      {selectedBooking.repair_cost > 0 && (
                        <div className="flex justify-between"><span className="text-[#676664]/50">Charge / RRP</span><span className="text-red-600">${selectedBooking.repair_cost.toFixed(2)}</span></div>
                      )}
                      {selectedBooking.remaining_payable > 0 && (
                        <div className="flex justify-between"><span className="text-[#676664]/50">Remaining Payable</span><span className="text-red-600 font-medium">${selectedBooking.remaining_payable.toFixed(2)}</span></div>
                      )}
                      {selectedBooking.invoice_id && (
                        <div className="flex justify-between items-center">
                          <span className="text-[#676664]/50">Invoice</span>
                          <span className="text-[#9A7B4F] text-[11px] flex items-center gap-1"><FileText className="w-3 h-3" />{selectedBooking.invoice_id.slice(0, 8).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={() => openDamageDialog(selectedBooking)}
                    variant="outline"
                    className="w-full border-[#282828]/10 text-[#676664] hover:bg-[#FAF8F0]"
                    data-testid="damage-assessment-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {selectedBooking.damage_status === "normal" ? "Assess Condition" : "Update Assessment"}
                  </Button>
                </div>
              )}
              
              {/* Email Resend Actions */}
              <div className="border border-[#282828]/10 p-4 space-y-3">
                <h3 className="text-[11px] text-[#676664] tracking-wide uppercase flex items-center gap-2">
                  <Mail className="w-3 h-3 text-[#9A7B4F]" />
                  Email Actions
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendEmail("booking_confirmation")}
                    disabled={emailResending !== null}
                    className="border-[#282828]/10 text-[#676664] hover:bg-[#9A7B4F]/10 hover:text-[#9A7B4F] hover:border-[#9A7B4F]/30 text-[11px] h-9"
                    data-testid="resend-customer-email-btn"
                  >
                    {emailResending === "booking_confirmation" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />}
                    Resend to Customer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendEmail("admin_notification")}
                    disabled={emailResending !== null}
                    className="border-[#282828]/10 text-[#676664] hover:bg-[#9A7B4F]/10 hover:text-[#9A7B4F] hover:border-[#9A7B4F]/30 text-[11px] h-9"
                    data-testid="resend-admin-email-btn"
                  >
                    {emailResending === "admin_notification" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />}
                    Send to Admin
                  </Button>
                </div>
              </div>

              {/* Admin Actions: Refund + Delete */}
              <div className="pt-4 border-t border-[#282828]/10 space-y-2">
                {selectedBooking.payment_status === "paid" && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setRefundAmount(selectedBooking.total_amount?.toString() || "");
                      setRefundReason("");
                      setIsRefundDialogOpen(true);
                    }} 
                    className="w-full border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5"
                    data-testid="admin-refund-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Refund Payment
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => setIsDeleteDialogOpen(true)} 
                  className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="delete-booking-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Delete Booking
                </Button>
              </div>
            </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {isExchangeOpen && selectedBooking && (
        <BookingExchangeModal
          booking={selectedBooking}
          onClose={() => setIsExchangeOpen(false)}
          onExchanged={() => { setIsExchangeOpen(false); fetchBookings(); setIsDialogOpen(false); }}
        />
      )}

      {/* Phase 5D — single Manual Booking modal (legacy modal removed) */}
      {showManualBooking && (
        <AdminManualBookingGroupModal
          onClose={() => setShowManualBooking(false)}
          onCreated={() => { setShowManualBooking(false); fetchBookings(); }}
        />
      )}

      {/* Phase 5D — Edit an existing manual booking group */}
      {editGroupId && (
        <AdminManualBookingGroupModal
          existingGroupId={editGroupId}
          onClose={() => setEditGroupId(null)}
          onCreated={() => { setEditGroupId(null); fetchBookings(); }}
        />
      )}

      {/* Legacy pseudo-group edit — promotes to a real MBG-* on save */}
      {editLegacyGroup && (
        <AdminManualBookingGroupModal
          existingGroupId={editLegacyGroup.anchorId}
          peerBookingIds={editLegacyGroup.peerIds}
          onClose={() => setEditLegacyGroup(null)}
          onCreated={() => { setEditLegacyGroup(null); fetchBookings(); }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl text-red-600">Delete Booking</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-6 mt-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-700">
                  Are you sure you want to permanently delete this booking? This action cannot be undone. All associated appointments and inspection data will also be removed.
                </p>
              </div>
              
              <div className="bg-[#FAF8F0] p-4 rounded-lg">
                <p className="text-sm text-[#676664]"><span className="text-[#676664]/50">Customer:</span> {selectedBooking.customer_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Outfit:</span> {selectedBooking.outfit_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Total:</span> ${selectedBooking.total_amount.toFixed(2)}</p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setIsDeleteDialogOpen(false)} 
                  className="flex-1 border-[#282828]/10"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteBooking} 
                  disabled={processing}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl text-[#9A7B4F]">Refund Payment</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-5 mt-4">
              <div className="bg-[#FAF8F0] p-4 rounded-lg text-sm text-[#676664] space-y-1">
                <p><span className="text-[#676664]/50">Customer:</span> {selectedBooking.customer_name}</p>
                <p><span className="text-[#676664]/50">Outfit:</span> {selectedBooking.outfit_name}</p>
                <p><span className="text-[#676664]/50">Total Paid:</span> <span className="font-medium text-[#282828]">${selectedBooking.total_amount.toFixed(2)}</span></p>
                {selectedBooking.rental_cost > 0 && <p><span className="text-[#676664]/50">Rental:</span> ${selectedBooking.rental_cost?.toFixed(2)}</p>}
                {selectedBooking.deposit_amount > 0 && <p><span className="text-[#676664]/50">Deposit:</span> ${selectedBooking.deposit_amount?.toFixed(2)}</p>}
              </div>
              <div>
                <label className="text-[11px] text-[#676664]/60 uppercase tracking-wider">Refund Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="w-full mt-1.5 px-3 py-2.5 border border-[#282828]/12 text-sm focus:ring-1 focus:ring-[#9A7B4F] focus:outline-none rounded"
                  placeholder={`Max: $${selectedBooking.total_amount.toFixed(2)}`}
                  data-testid="refund-amount-input"
                />
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setRefundAmount(selectedBooking.total_amount?.toString())} className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/20 px-2 py-1 hover:bg-[#9A7B4F]/5">Full (${selectedBooking.total_amount.toFixed(2)})</button>
                  {selectedBooking.rental_cost > 0 && <button type="button" onClick={() => setRefundAmount(selectedBooking.rental_cost?.toString())} className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/20 px-2 py-1 hover:bg-[#9A7B4F]/5">Rental Only (${selectedBooking.rental_cost?.toFixed(2)})</button>}
                  {selectedBooking.deposit_amount > 0 && <button type="button" onClick={() => setRefundAmount(selectedBooking.deposit_amount?.toString())} className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/20 px-2 py-1 hover:bg-[#9A7B4F]/5">Deposit Only (${selectedBooking.deposit_amount?.toFixed(2)})</button>}
                </div>
              </div>
              <div>
                <label className="text-[11px] text-[#676664]/60 uppercase tracking-wider">Reason</label>
                <input
                  type="text"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className="w-full mt-1.5 px-3 py-2.5 border border-[#282828]/12 text-sm focus:ring-1 focus:ring-[#9A7B4F] focus:outline-none rounded"
                  placeholder="e.g. Customer cancellation, item defect..."
                  data-testid="refund-reason-input"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsRefundDialogOpen(false)} className="flex-1 border-[#282828]/10">Cancel</Button>
                <Button
                  onClick={handleFullRefund}
                  disabled={processing || !refundAmount || parseFloat(refundAmount) <= 0}
                  className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
                  data-testid="confirm-refund-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
                  Refund ${refundAmount ? parseFloat(refundAmount).toFixed(2) : "0.00"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Return Dialog */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl text-[#676664]">Record Item Return</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-6 mt-4">
              <div className="bg-[#FAF8F0] p-4 rounded-lg">
                <p className="text-sm text-[#676664]"><span className="text-[#676664]/50">Outfit:</span> {selectedBooking.outfit_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Customer:</span> {selectedBooking.customer_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Return By:</span> {selectedBooking.return_by_date ? format(new Date(selectedBooking.return_by_date + "T00:00:00"), "EEE, MMM d, yyyy") : "N/A"}</p>
              </div>
              
              <div>
                <Label className="text-[#676664]/60 text-[11px]">Actual Return Date</Label>
                <Input
                  type="date"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  className="mt-2 border-[#282828]/10"
                  data-testid="return-date-input"
                />
              </div>
              
              {selectedBooking.return_by_date && returnDate > selectedBooking.return_by_date && (
                <div className="bg-red-50 p-4 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-700 font-medium">Late Return</p>
                    <p className="text-xs text-red-600 mt-1">
                      This return is late. A fee of $40/day will be deducted from the security bond*.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setIsReturnDialogOpen(false)} 
                  className="flex-1 border-[#282828]/10"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleRecordReturn} 
                  disabled={processing}
                  className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
                  data-testid="confirm-return-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirm Return
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Security Bond Action Dialog */}
      <Dialog open={isBondDialogOpen} onOpenChange={setIsBondDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl text-[#676664]">Manage Security Bond*</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-5 mt-4">
              <div className="bg-[#FAF8F0] p-4">
                <p className="text-sm text-[#676664]"><span className="text-[#676664]/50">Outfit:</span> {selectedBooking.outfit_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Bond Held:</span> <span className="text-[#9A7B4F] font-medium">${selectedBooking.deposit_amount.toFixed(2)}</span></p>
              </div>
              {bondRefundNotice(selectedBooking)}

              <div>
                <Label className="text-[#676664]/60 text-[11px] mb-2 block">Action</Label>
                <Select value={bondAction} onValueChange={(v) => { setBondAction(v); if (v === "refund_full") setBondRefundAmount(refundableBond(selectedBooking).toString()); if (v === "retain") setBondRefundAmount("0"); }}>
                  <SelectTrigger className="border-[#282828]/10" data-testid="bond-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="refund_full">Refund Full Bond</SelectItem>
                    <SelectItem value="refund_partial">Partial Refund</SelectItem>
                    <SelectItem value="retain">Retain Bond (Damage/Loss)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {bondAction === "refund_partial" && (
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Refund Amount ($)</Label>
                  <Input type="number" step="0.01" min="0" max={selectedBooking.deposit_amount} value={bondRefundAmount} onChange={(e) => setBondRefundAmount(e.target.value)} className="mt-2 border-[#282828]/10" data-testid="bond-refund-amount" />
                </div>
              )}

              <div>
                <Label className="text-[#676664]/60 text-[11px]">Reason / Notes</Label>
                <Input value={bondReason} onChange={(e) => setBondReason(e.target.value)} className="mt-2 border-[#282828]/10" placeholder={bondAction === "retain" ? "e.g., Stain on dupatta" : "Optional notes"} data-testid="bond-reason-input" />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsBondDialogOpen(false)} className="flex-1 border-[#282828]/10">Cancel</Button>
                <Button onClick={handleBondAction} disabled={processing} className={`flex-1 text-white ${bondAction === "retain" ? "bg-red-600 hover:bg-red-700" : "bg-[#9A7B4F] hover:bg-[#8B6B3F]"}`} data-testid="confirm-bond-action-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
                  {bondAction === "refund_full" ? "Refund Full Bond" : bondAction === "refund_partial" ? `Refund $${bondRefundAmount || 0}` : "Retain Bond"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Damage Assessment Dialog */}
      <Dialog open={isDamageDialogOpen} onOpenChange={setIsDamageDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl text-[#676664]">Damage Assessment</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-5 mt-4">
              <div className="bg-[#FAF8F0] p-4">
                <p className="text-sm text-[#676664]"><span className="text-[#676664]/50">Outfit:</span> {selectedBooking.outfit_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Bond Held:</span> <span className="text-[#9A7B4F] font-medium">${selectedBooking.deposit_amount?.toFixed(2)}</span></p>
                {selectedBooking.rrp_at_booking > 0 && (
                  <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">RRP:</span> ${selectedBooking.rrp_at_booking?.toFixed(2)}</p>
                )}
              </div>

              <div>
                <Label className="text-[#676664]/60 text-[11px] mb-2 block">Condition Status</Label>
                <Select value={damageStatus} onValueChange={(v) => { setDamageStatus(v); setWaiveCharges(false); }}>
                  <SelectTrigger className="border-[#282828]/10" data-testid="damage-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="slightly_damaged">Slightly Damaged</SelectItem>
                    <SelectItem value="irreparable">Irreparable</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="not_returned">Not Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {damageStatus === "slightly_damaged" && !waiveCharges && (
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">Repair Cost ($)</Label>
                  <Input type="number" step="0.01" min="0" value={repairCost} onChange={(e) => setRepairCost(e.target.value)} className="mt-2 border-[#282828]/10" placeholder="e.g., 80.00" data-testid="repair-cost-input" />
                  {repairCost && parseFloat(repairCost) > 0 && (
                    <div className="mt-2 text-[12px] space-y-1 bg-[#FAF8F0] p-3">
                      <div className="flex justify-between"><span className="text-[#676664]/60">Repair Cost</span><span>${parseFloat(repairCost).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-[#676664]/60">Bond Held</span><span>${selectedBooking.deposit_amount?.toFixed(2)}</span></div>
                      <div className="flex justify-between border-t border-[#282828]/10 pt-1 mt-1">
                        {parseFloat(repairCost) <= selectedBooking.deposit_amount ? (
                          <><span className="text-green-700">Bond Refund</span><span className="text-green-700">${(selectedBooking.deposit_amount - parseFloat(repairCost)).toFixed(2)}</span></>
                        ) : (
                          <><span className="text-red-600 font-medium">Remaining Payable</span><span className="text-red-600 font-medium">${(parseFloat(repairCost) - selectedBooking.deposit_amount).toFixed(2)}</span></>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {["irreparable", "lost", "not_returned"].includes(damageStatus) && !waiveCharges && (
                <div>
                  <Label className="text-[#676664]/60 text-[11px]">RRP Override ($)</Label>
                  <Input type="number" step="0.01" min="0" value={rrpOverride} onChange={(e) => setRrpOverride(e.target.value)} className="mt-2 border-[#282828]/10" placeholder={selectedBooking.rrp_at_booking ? `Current: $${selectedBooking.rrp_at_booking}` : "Enter RRP"} data-testid="rrp-override-input" />
                  {rrpOverride && parseFloat(rrpOverride) > 0 && (
                    <div className="mt-2 text-[12px] space-y-1 bg-red-50 p-3">
                      <div className="flex justify-between"><span className="text-[#676664]/60">RRP</span><span>${parseFloat(rrpOverride).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-[#676664]/60">Bond Applied</span><span>-${Math.min(selectedBooking.deposit_amount, parseFloat(rrpOverride)).toFixed(2)}</span></div>
                      <div className="flex justify-between border-t border-red-200 pt-1 mt-1">
                        {selectedBooking.deposit_amount >= parseFloat(rrpOverride) ? (
                          <><span className="text-green-700">Excess Bond Refund</span><span className="text-green-700">${(selectedBooking.deposit_amount - parseFloat(rrpOverride)).toFixed(2)}</span></>
                        ) : (
                          <><span className="text-red-600 font-medium">Remaining Payable</span><span className="text-red-600 font-medium">${(parseFloat(rrpOverride) - selectedBooking.deposit_amount).toFixed(2)}</span></>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {damageStatus !== "normal" && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={waiveCharges} onChange={(e) => setWaiveCharges(e.target.checked)} className="rounded border-[#282828]/20 w-4 h-4 accent-[#9A7B4F]" data-testid="waive-charges-checkbox" />
                  <span className="text-[13px] text-[#676664]">Waive all charges (full bond refund)</span>
                </label>
              )}

              <div>
                <Label className="text-[#676664]/60 text-[11px]">Notes</Label>
                <Input value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)} className="mt-2 border-[#282828]/10" placeholder="Describe the condition..." data-testid="damage-notes-input" />
              </div>

              {(waiveCharges || rrpOverride !== String(selectedBooking.rrp_at_booking || "")) && damageStatus !== "normal" && (
                <div>
                  <Label className="text-red-500 text-[11px]">Override Reason (required for audit)</Label>
                  <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="mt-2 border-red-200" placeholder="Reason for override..." data-testid="override-reason-input" />
                </div>
              )}

              {auditLogs.length > 0 && (
                <div className="border border-[#282828]/10">
                  <button onClick={() => setShowAuditLog(!showAuditLog)} className="w-full flex items-center justify-between p-3 text-[11px] text-[#676664]/60 uppercase tracking-wider hover:bg-[#FAF8F0]">
                    <span>Audit Trail ({auditLogs.length})</span>
                    {showAuditLog ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {showAuditLog && (
                    <div className="px-3 pb-3 space-y-2 max-h-40 overflow-y-auto">
                      {auditLogs.map((log) => (
                        <div key={log.id} className="text-[11px] bg-[#FAF8F0] p-2">
                          <div className="flex justify-between text-[#676664]/50">
                            <span>{log.admin_email}</span>
                            <span>{format(new Date(log.timestamp), "MMM d, HH:mm")}</span>
                          </div>
                          <p className="text-[#676664] mt-0.5">
                            {log.old_value?.damage_status} &rarr; {log.new_value?.damage_status}
                            {log.override_reason && <span className="text-[#9A7B4F] ml-1">({log.override_reason})</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsDamageDialogOpen(false)} className="flex-1 border-[#282828]/10">Cancel</Button>
                <Button 
                  onClick={handleDamageAssessment} 
                  disabled={damageProcessing}
                  className={`flex-1 text-white ${["irreparable","lost","not_returned"].includes(damageStatus) && !waiveCharges ? "bg-red-600 hover:bg-red-700" : "bg-[#9A7B4F] hover:bg-[#8B6B3F]"}`}
                  data-testid="confirm-damage-btn"
                >
                  {damageProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                  Save Assessment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inspection Photo & Condition Dialog */}
      <Dialog open={isInspectionDialogOpen} onOpenChange={setIsInspectionDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl text-[#676664]">Return Inspection</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-5 mt-4">
              <div className="bg-blue-50 p-4">
                <p className="text-sm text-[#676664]"><span className="text-[#676664]/50">Outfit:</span> {selectedBooking.outfit_name}</p>
                <p className="text-sm text-[#676664] mt-1"><span className="text-[#676664]/50">Customer:</span> {selectedBooking.customer_name}</p>
                {existingInspection && <p className="text-sm text-blue-600 mt-1">Previous inspection exists - editing will update it</p>}
              </div>
              <div>
                <Label className="text-[#676664]/60 text-[11px] mb-2 block">Return Condition</Label>
                <Select value={inspectionCondition} onValueChange={setInspectionCondition}>
                  <SelectTrigger className="border-[#282828]/10" data-testid="inspection-condition-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good - No Damage</SelectItem>
                    <SelectItem value="minor_damage">Minor Damage</SelectItem>
                    <SelectItem value="major_damage">Major Damage</SelectItem>
                    <SelectItem value="lost">Lost / Not Returned</SelectItem>
                  </SelectContent>
                </Select>
                {inspectionCondition === "good" && (
                  <p className="text-[10px] text-green-600 mt-1">Selecting "Good" will auto-refund the security deposit when saved.</p>
                )}
              </div>
              <div>
                <Label className="text-[#676664]/60 text-[11px]">Notes</Label>
                <Input value={inspectionNotes} onChange={(e) => setInspectionNotes(e.target.value)} className="mt-2 border-[#282828]/10" placeholder="Inspection notes..." data-testid="inspection-notes-input" />
              </div>

              {/* Photo Upload */}
              <div>
                <Label className="text-[#676664]/60 text-[11px] mb-2 block">Evidence Photos ({inspectionPhotos.length})</Label>
                {inspectionPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {inspectionPhotos.map((url, i) => (
                      <div key={i} className="aspect-square bg-[#f5f3f0] overflow-hidden border border-[#282828]/10 relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api${url}`} alt={`Inspection ${i + 1}`} className="w-full h-full object-cover" />
                        <button onClick={() => setInspectionPhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">&times;</button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed border-[#282828]/15 text-[12px] text-[#676664]/50 hover:border-[#9A7B4F]/30 hover:text-[#9A7B4F] cursor-pointer transition-colors" data-testid="upload-photo-label">
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                  <input type="file" accept="image/*" onChange={handleUploadPhoto} className="hidden" disabled={uploadingPhoto} />
                </label>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsInspectionDialogOpen(false)} className="flex-1 border-[#282828]/10">Cancel</Button>
                <Button onClick={handleSaveInspection} disabled={inspectionProcessing} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-inspection-btn">
                  {inspectionProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                  Save Inspection
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ──────────── Phase 1 — Edit Payment Summary modal ──────────── */}
      <Dialog open={isPaymentSummaryOpen} onOpenChange={setIsPaymentSummaryOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#676664]">Edit Payment Summary</DialogTitle>
          </DialogHeader>
          {paymentSummaryDraft && (
            <div className="space-y-5 mt-3">
              <p className="text-[11px] text-[#676664]/60 -mt-2">All changes are audit-logged. Communications + reporting use these values.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-3">
                  <h4 className="text-[10px] uppercase tracking-wider text-[#676664]/80 font-semibold">Rental Payment</h4>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Method</Label>
                    <Select value={paymentSummaryDraft.rental_payment_method} onValueChange={(v) => setPaymentSummaryDraft({ ...paymentSummaryDraft, rental_payment_method: v })}>
                      <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="rental-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{RENTAL_METHOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Status</Label>
                    <Select value={paymentSummaryDraft.rental_payment_status} onValueChange={(v) => setPaymentSummaryDraft({ ...paymentSummaryDraft, rental_payment_status: v })}>
                      <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="rental-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{RENTAL_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Amount Received ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={paymentSummaryDraft.rental_amount_paid}
                      onChange={(e) => setPaymentSummaryDraft({ ...paymentSummaryDraft, rental_amount_paid: e.target.value })}
                      className="mt-1 border-[#282828]/10"
                      placeholder="e.g. 120.00"
                      data-testid="rental-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Received Date</Label>
                    <Input
                      type="date"
                      value={paymentSummaryDraft.rental_paid_at}
                      onChange={(e) => setPaymentSummaryDraft({ ...paymentSummaryDraft, rental_paid_at: e.target.value })}
                      className="mt-1 border-[#282828]/10"
                      data-testid="rental-paid-at-input"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="text-[10px] uppercase tracking-wider text-[#676664]/80 font-semibold">Bond / Deposit</h4>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Method</Label>
                    <Select value={paymentSummaryDraft.bond_collection_method} onValueChange={(v) => setPaymentSummaryDraft({ ...paymentSummaryDraft, bond_collection_method: v })}>
                      <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="bond-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{BOND_METHOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Status</Label>
                    <Select value={paymentSummaryDraft.bond_status_v2} onValueChange={(v) => setPaymentSummaryDraft({ ...paymentSummaryDraft, bond_status_v2: v })}>
                      <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="bond-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{BOND_STATUS_V2_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Bond Amount ($)</Label>
                    <Input
                      type="text"
                      value={`$${(selectedBooking?.deposit_amount || 0).toFixed(2)}`}
                      disabled
                      className="mt-1 border-[#282828]/10 bg-[#FAF8F0]"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Collected Date</Label>
                    <Input
                      type="date"
                      value={paymentSummaryDraft.bond_collected_at}
                      onChange={(e) => setPaymentSummaryDraft({ ...paymentSummaryDraft, bond_collected_at: e.target.value })}
                      className="mt-1 border-[#282828]/10"
                      data-testid="bond-collected-at-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Internal Payment Notes</Label>
                <textarea
                  rows={3}
                  value={paymentSummaryDraft.internal_payment_notes}
                  onChange={(e) => setPaymentSummaryDraft({ ...paymentSummaryDraft, internal_payment_notes: e.target.value })}
                  placeholder="Bank ref #, terminal ID, etc. (admin-only)"
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm font-light bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="internal-payment-notes-input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsPaymentSummaryOpen(false)} className="flex-1 border-[#282828]/10" data-testid="payment-summary-cancel">Cancel</Button>
                <Button onClick={savePaymentSummary} disabled={paymentSummarySaving} className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="payment-summary-save">
                  {paymentSummarySaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ──────────── Phase 2 — Bond Refund modal (External or Stripe) ──────────── */}
      <Dialog open={bondRefundOpen} onOpenChange={setBondRefundOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#676664]">Refund Bond</DialogTitle>
          </DialogHeader>
          {bondRefundDraft && selectedBooking && (
            <div className="space-y-5 mt-3">
              {bondRefundNotice(selectedBooking)}
              <p className="text-[11px] text-[#676664]/60 -mt-2">
                Bond held: <strong className="text-[#9A7B4F]">${(Number(selectedBooking.deposit_amount) || 0).toFixed(2)}</strong>.
                Choose how this refund is being processed.
              </p>

              {/* Refund Path */}
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/70 font-semibold mb-2 block">Refund Path</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { v: "external", t: "Refund Externally (no Stripe)", d: "Use when the original bond was taken via bank/cash/EFTPOS. Records refund and sends the Bond Refunded email." },
                    { v: "stripe", t: "Refund via Stripe", d: "Reverses the original Stripe charge. Only valid when the bond was collected via Stripe." },
                  ].map((opt) => (
                    <label key={opt.v}
                      className={`flex items-start gap-2 p-3 border cursor-pointer ${bondRefundDraft.refund_path === opt.v ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/10 hover:bg-[#FAF8F0]"}`}
                      data-testid={`refund-path-${opt.v}`}
                    >
                      <input
                        type="radio"
                        name="bondRefundPath"
                        checked={bondRefundDraft.refund_path === opt.v}
                        onChange={() => setBondRefundDraft({ ...bondRefundDraft, refund_path: opt.v, refund_method: opt.v === "stripe" ? "stripe" : (selectedBooking.bond_collection_method && selectedBooking.bond_collection_method !== "other" && selectedBooking.bond_collection_method !== "stripe" ? selectedBooking.bond_collection_method : "bank_transfer") })}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-[12px] font-semibold text-[#282828]">{opt.t}</div>
                        <div className="text-[11px] text-[#676664]/70 mt-0.5">{opt.d}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Refund Type */}
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/70 font-semibold mb-2 block">Refund Type</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { v: "full_refund", t: "Full Refund", d: `Refund the entire $${refundableBond(selectedBooking).toFixed(2)} refundable bond.` },
                    { v: "partial_refund", t: "Partial Refund", d: "Refund some now; the rest stays held for later refunds or deductions." },
                    { v: "full_deduction", t: "Full Deduction", d: "Retain the entire bond against damage/loss." },
                    { v: "partial_deduction", t: "Partial Deduction", d: "Specify refund + deduction amounts independently." },
                  ].map((opt) => (
                    <label key={opt.v}
                      className={`flex items-start gap-2 p-3 border cursor-pointer ${bondRefundDraft.refund_type === opt.v ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/10 hover:bg-[#FAF8F0]"}`}
                      data-testid={`refund-type-${opt.v}`}
                    >
                      <input
                        type="radio"
                        name="bondRefundType"
                        checked={bondRefundDraft.refund_type === opt.v}
                        onChange={() => applyBondRefundType(opt.v)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-[12px] font-semibold text-[#282828]">{opt.t}</div>
                        <div className="text-[11px] text-[#676664]/70 mt-0.5">{opt.d}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Amounts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Refund Amount ($)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={bondRefundDraft.refund_amount}
                    onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, refund_amount: e.target.value })}
                    disabled={["full_refund", "full_deduction"].includes(bondRefundDraft.refund_type)}
                    className="mt-1 border-[#282828]/10"
                    data-testid="bond-refund-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Deduction Amount ($)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={bondRefundDraft.deduction_amount}
                    onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, deduction_amount: e.target.value })}
                    disabled={["full_refund", "full_deduction", "partial_refund"].includes(bondRefundDraft.refund_type)}
                    className="mt-1 border-[#282828]/10"
                    data-testid="bond-deduction-amount-input"
                  />
                </div>
              </div>

              {/* Method + Date (external path only) */}
              {bondRefundDraft.refund_path === "external" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Refund Method</Label>
                    <Select value={bondRefundDraft.refund_method} onValueChange={(v) => setBondRefundDraft({ ...bondRefundDraft, refund_method: v })}>
                      <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="bond-refund-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="eftpos">EFTPOS</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Refund Date</Label>
                    <Input
                      type="date"
                      value={bondRefundDraft.refund_date}
                      onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, refund_date: e.target.value })}
                      className="mt-1 border-[#282828]/10"
                      data-testid="bond-refund-date-input"
                    />
                  </div>
                </div>
              )}

              {/* Reason + Notes */}
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Deduction Reason</Label>
                <textarea
                  rows={2}
                  value={bondRefundDraft.deduction_reason}
                  onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, deduction_reason: e.target.value })}
                  placeholder={["full_refund"].includes(bondRefundDraft.refund_type) ? "Optional — e.g. Returned in good condition" : "e.g. Stain on dupatta — $50 dry-clean fee retained"}
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="bond-deduction-reason-input"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Customer-Facing Notes</Label>
                <textarea
                  rows={2}
                  value={bondRefundDraft.customer_notes}
                  onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, customer_notes: e.target.value })}
                  placeholder="Shown to customer alongside the refund"
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="bond-customer-notes-input"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Internal Notes (admin-only)</Label>
                <textarea
                  rows={2}
                  value={bondRefundDraft.internal_notes}
                  onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, internal_notes: e.target.value })}
                  placeholder="Not shared with customer"
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="bond-internal-notes-input"
                />
              </div>

              {/* Notify */}
              <label className="flex items-start gap-2 text-[12px] text-[#282828]">
                <input
                  type="checkbox"
                  checked={bondRefundDraft.notify_customer}
                  onChange={(e) => setBondRefundDraft({ ...bondRefundDraft, notify_customer: e.target.checked })}
                  className="mt-1"
                  data-testid="notify-customer-checkbox"
                />
                <span>
                  Notify customer — send the standard <strong>Bond Refunded</strong> email immediately.
                  <span className="text-[#676664]/60"> Uncheck to record the refund without notifying (e.g. transfer not yet cleared).</span>
                </span>
              </label>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setBondRefundOpen(false)} className="flex-1 border-[#282828]/10" data-testid="bond-refund-cancel">Cancel</Button>
                <Button onClick={submitBondRefund} disabled={bondRefundSaving} className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="bond-refund-submit">
                  {bondRefundSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                  Record Refund
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ──────────── Phase 3 — Create Charge modal ──────────── */}
      <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#676664]">
              {chargeDraft ? `New ${CHARGE_TYPE_OPTIONS.find(o => o.value === chargeDraft.charge_type)?.label || "Charge"}` : "New Charge"}
            </DialogTitle>
          </DialogHeader>
          {chargeDraft && (
            <div className="space-y-5 mt-3">
              <p className="text-[11px] text-[#676664]/60 -mt-2">Records a charge against this booking. Choose how it will be collected.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Charge Type</Label>
                  <Select value={chargeDraft.charge_type} onValueChange={(v) => setChargeDraft({ ...chargeDraft, charge_type: v })}>
                    <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="charge-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CHARGE_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Amount ($)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={chargeDraft.amount}
                    onChange={(e) => setChargeDraft({ ...chargeDraft, amount: e.target.value })}
                    placeholder="e.g. 45.00"
                    className="mt-1 border-[#282828]/10"
                    data-testid="charge-amount-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Customer-Visible Description</Label>
                <textarea
                  rows={2}
                  value={chargeDraft.description}
                  onChange={(e) => setChargeDraft({ ...chargeDraft, description: e.target.value })}
                  placeholder="Shown to customer on invoice / email"
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="charge-description-input"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Customer-Facing Notes</Label>
                <textarea
                  rows={2}
                  value={chargeDraft.customer_notes}
                  onChange={(e) => setChargeDraft({ ...chargeDraft, customer_notes: e.target.value })}
                  placeholder="Extra context for the customer (optional)"
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="charge-customer-notes-input"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Internal Notes (admin-only)</Label>
                <textarea
                  rows={2}
                  value={chargeDraft.internal_notes}
                  onChange={(e) => setChargeDraft({ ...chargeDraft, internal_notes: e.target.value })}
                  placeholder="Tailor quote, repair vendor, etc. — not shared with customer"
                  className="mt-1 w-full border border-[#282828]/10 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/40"
                  data-testid="charge-internal-notes-input"
                />
              </div>

              <div>
                <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Photo URLs (optional)</Label>
                <Input
                  type="text"
                  value={(chargeDraft.photos || []).join(", ")}
                  onChange={(e) => setChargeDraft({ ...chargeDraft, photos: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                  placeholder="Paste image URLs, comma-separated"
                  className="mt-1 border-[#282828]/10"
                  data-testid="charge-photos-input"
                />
                <p className="text-[10px] text-[#676664]/40 mt-1">Tip: Use inspection-photo URLs from the Inspection tab.</p>
              </div>

              <hr className="border-[#282828]/10" />

              <ChargeCollectionOptions draft={chargeDraft} setDraft={setChargeDraft} meta={chargesMeta} />

              <label className="flex items-start gap-2 text-[12px] text-[#282828]">
                <input
                  type="checkbox"
                  checked={chargeDraft.notify_customer}
                  onChange={(e) => setChargeDraft({ ...chargeDraft, notify_customer: e.target.checked })}
                  className="mt-1"
                  data-testid="charge-notify-checkbox"
                />
                <span>
                  Email the customer
                  {chargeDraft.collection_mode === "stripe" && <span className="text-[#9A7B4F]"> (includes the Stripe payment link)</span>}
                  {["external_paid", "stripe_saved_card"].includes(chargeDraft.collection_mode) && <span className="text-[#0E7C5A]"> (receipt — only sent once payment succeeds)</span>}
                  {chargeDraft.collection_mode === "bond_deduction" && <span className="text-[#9A7B4F]"> (bond deduction notice with remaining bond)</span>}
                  .
                </span>
              </label>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setChargeOpen(false)} className="flex-1 border-[#282828]/10" data-testid="charge-cancel">Cancel</Button>
                <Button onClick={submitCharge} disabled={chargeSaving} className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="charge-submit">
                  {chargeSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Banknote className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                  Create Charge
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditChargeDialog
        charge={editingCharge}
        api={API}
        headers={getAuthHeaders()}
        onClose={() => setEditingCharge(null)}
        onSaved={() => { setEditingCharge(null); fetchCharges(selectedBooking?.id); fetchOutstanding(); }}
      />

      {smsModal && (
        <SmsReplyModal
          to={smsModal}
          title="Send SMS to customer"
          template_key="admin_direct_booking_sms"
          onClose={() => setSmsModal(null)}
          onSent={() => setSmsModal(null)}
        />
      )}

    </div>
  );
}
